import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useRoles } from '../../hooks/useRoles';
import AdminLayout from '../../components/layout/AdminLayout';
import OpsLayout from '../../components/layout/OpsLayout';
import DeptBadge from '../../components/DeptBadge';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import AdjustShiftTimeDialog from '../../components/AdjustShiftTimeDialog';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { 
  CalendarPlus, 
  Plus,
  MapPin,
  Trash2,
  Clock,
  Briefcase,
  Shirt,
  User,
  Users,
  CheckCircle,
  CheckCircle2,
  Search,
  PoundSterling,
  X,
  UserPlus,
  Hand,
  Edit,
  Bell,
  Megaphone,
  Download,
  Shield,
  Star,
  Check,
  XCircle,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Tent,
  Home,
  Car,
  AlertTriangle,
  QrCode,
  Mail,
  Archive,
  FileSpreadsheet,
  MoreVertical,
  Settings,
  Eye,
  Replace as ReplaceIcon,
  Layers,
  Loader2,
  Save as SaveIcon,
  LogIn,
  LogOut,
  LayoutGrid
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { cn } from '../../lib/utils';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { useAuth } from '../../contexts/AuthContext';
import ImportExcelDialog from '../../components/ImportExcelDialog';
import EventScheduleDialog from '../../components/EventScheduleDialog';
import StaffScheduleViewDialog from '../../components/StaffScheduleViewDialog';
import ReplaceStaffDialog from '../../components/ReplaceStaffDialog';
import ManualClockDialog from '../../components/ManualClockDialog';
import RotaAllocationSection from '../../components/RotaAllocationSection';
import MapPickerDialog from '../../components/MapPickerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-role headcount breakdown block shown inside the Create Open Shifts,
// Edit Event and Manage Event (batch edit) dialogs. Total is auto-computed
// as the sum of the four inputs and drives the open_shift's `quantity` on
// save. Kept as a pure/controlled component to reuse the same UI everywhere.
//
// Row list is now DYNAMIC — sourced from the caller (see `roles` prop, wired
// to `useRoles().activeRoles` at the top of the page). Falls back to the 4
// canonical seeds when no roles have loaded yet so the dialog never renders
// empty on the first tick.
const SEED_STAFF_REQUIREMENT_ROWS = [
  { key: 'sia_door_supervisor', label: 'SIA Door Supervisors' },
  { key: 'cso_steward', label: 'CSO Stewards' },
  { key: 'cleaner', label: 'Cleaners' },
  { key: 'supervisor', label: 'Supervisors' },
];

// ISO 4217 currency options exposed in the per-role rate block. Kept in sync
// with backend `SUPPORTED_CURRENCIES` — unknown values are coerced to GBP.
const CURRENCY_OPTIONS = [
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
];
const currencySymbol = (code) =>
  CURRENCY_OPTIONS.find((c) => c.code === code)?.symbol || '£';


function AssignStaffNowBlock({ value = [], onChange, users = [] }) {
  const [query, setQuery] = useState('');
  const activeUsers = (users || []).filter(
    (u) => !u.deleted_at && u.status !== 'suspended' && (u.role === 'employee' || u.role === 'supervisor')
  );
  const filtered = activeUsers.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  const remove = (id) => onChange(value.filter((v) => v !== id));
  const selectedUsers = activeUsers.filter((u) => value.includes(u.id));

  return (
    <div className="space-y-2 p-3 bg-emerald-500/5 border border-emerald-500/30 rounded-sm" data-testid="assign-staff-now-block">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">
            Assign staff now (skip approval)
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Optional. Employees added here are placed directly on every day of this event.
            No claim request is created. Leave empty if you want employees to request themselves.
          </p>
        </div>
        {value.length > 0 && (
          <span className="text-[10px] uppercase text-emerald-200 bg-emerald-500/10 px-2 py-1 rounded-sm">
            {value.length} selected
          </span>
        )}
      </div>

      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedUsers.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-100 text-[11px]"
              data-testid={`assigned-chip-${u.id}`}
            >
              {u.name}
              <button
                type="button"
                onClick={() => remove(u.id)}
                className="hover:text-red-300 text-emerald-200 pl-1"
                aria-label={`Remove ${u.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        placeholder="Search employees by name or email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="w-full h-9 px-2 rounded-sm bg-background border border-border text-sm"
        data-testid="assign-staff-now-search"
      />

      <div className="max-h-40 overflow-y-auto border border-border/60 rounded-sm bg-background/40">
        {filtered.length === 0 && (
          <p className="text-[11px] text-muted-foreground p-2">No matching employees.</p>
        )}
        {filtered.map((u) => {
          const isSelected = value.includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => toggle(u.id)}
              className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-muted/40 ${isSelected ? 'bg-emerald-500/5' : ''}`}
              data-testid={`assign-toggle-${u.id}`}
            >
              <div className="min-w-0">
                <span className="text-foreground truncate block">{u.name}</span>
                <span className="text-muted-foreground text-[10px] truncate block">{u.email}</span>
              </div>
              <span className={`ml-2 shrink-0 text-[10px] uppercase ${isSelected ? 'text-emerald-300' : 'text-muted-foreground'}`}>
                {isSelected ? '✓ Added' : 'Add'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function StaffRequirementsBlock({
  value, onChange,
  rates, onRatesChange,
  currency, onCurrencyChange,
  roles,
  testIdPrefix = 'staff', optional = false, helperText
}) {
  const safe = value || {};
  const safeRates = rates || {};
  const safeCurrency = currency || 'GBP';
  const symbol = currencySymbol(safeCurrency);
  // Row list is dynamic — comes from Role Management. Falls back to seed rows
  // while roles are still loading so the dialog never renders empty.
  const rows = (roles && roles.length > 0)
    ? roles.map((r) => ({ key: r.slug, label: r.name, defaultRate: r.default_hourly_rate }))
    : SEED_STAFF_REQUIREMENT_ROWS.map((r) => ({ ...r, defaultRate: 0 }));
  const total = rows.reduce(
    (sum, r) => sum + (parseInt(safe[r.key]) || 0),
    0
  );
  const handleChange = (key, raw) => {
    // Allow empty input while typing but persist as integer >=0.
    const n = raw === '' ? 0 : Math.max(0, parseInt(raw) || 0);
    onChange({ ...safe, [key]: n });
  };
  const handleRateChange = (key, raw) => {
    if (!onRatesChange) return;
    // Rates are floats (£/€/$/hr). Empty input → 0.
    const n = raw === '' ? 0 : Math.max(0, parseFloat(raw) || 0);
    onRatesChange({ ...safeRates, [key]: n });
  };
  return (
    <div
      className="space-y-3 p-3 bg-muted/50 border border-border rounded-sm"
      data-testid={`${testIdPrefix}-staff-requirements`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
            Staff Requirements
          </Label>
        </div>
        {onCurrencyChange && (
          <div className="flex items-center gap-2">
            <Label
              htmlFor={`${testIdPrefix}-currency`}
              className="text-[10px] uppercase tracking-wider text-muted-foreground/70"
            >
              Currency
            </Label>
            <Select value={safeCurrency} onValueChange={(val) => onCurrencyChange(val)}>
              <SelectTrigger
                id={`${testIdPrefix}-currency`}
                data-testid={`${testIdPrefix}-currency`}
                className="h-7 min-w-[110px] bg-background border-border rounded-sm text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {CURRENCY_OPTIONS.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="space-y-2 max-h-[320px] overflow-y-auto">
        {rows.map((row) => (
          <div key={row.key} className="grid grid-cols-[1fr_90px_110px] gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground/80" htmlFor={`${testIdPrefix}-req-${row.key}`}>
                {row.label}
              </Label>
              <Input
                id={`${testIdPrefix}-req-${row.key}`}
                type="number"
                min="0"
                value={safe[row.key] ?? 0}
                onChange={(e) => handleChange(row.key, e.target.value)}
                className="bg-background border-border rounded-sm h-9"
                data-testid={`${testIdPrefix}-req-${row.key}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">
                Staff
              </Label>
              <div className="h-9 flex items-center justify-center text-sm text-muted-foreground/80 tabular-nums font-medium border border-transparent">
                × {safe[row.key] || 0}
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground/60 uppercase tracking-wider" htmlFor={`${testIdPrefix}-rate-${row.key}`}>
                {symbol}/hr
              </Label>
              <Input
                id={`${testIdPrefix}-rate-${row.key}`}
                type="number"
                min="0"
                step="0.01"
                value={safeRates[row.key] ?? ''}
                placeholder={row.defaultRate > 0 ? String(row.defaultRate.toFixed(2)) : '0.00'}
                onChange={(e) => handleRateChange(row.key, e.target.value)}
                className="bg-background border-border rounded-sm h-9"
                data-testid={`${testIdPrefix}-rate-${row.key}`}
              />
            </div>
          </div>
        ))}
      </div>
      <div
        className="flex items-center justify-between px-2 py-1.5 border-t border-border/60"
        data-testid={`${testIdPrefix}-req-total-row`}
      >
        <span className="text-xs uppercase tracking-wider text-muted-foreground">
          Total Staff Required
        </span>
        <span
          className="text-lg font-semibold text-foreground tabular-nums"
          data-testid={`${testIdPrefix}-req-total`}
        >
          {total}
        </span>
      </div>
      {helperText ? (
        <p className="text-[11px] text-muted-foreground/70">{helperText}</p>
      ) : optional ? null : (
        <p className="text-[11px] text-muted-foreground/70">
          Each role can have its own {symbol}/hr rate. Leave empty to inherit the event-level Hourly Rate.
        </p>
      )}
    </div>
  );
}

export default function UpcomingShiftsPage({ opsMode = false }) {
  const Layout = opsMode ? OpsLayout : AdminLayout;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeRoles } = useRoles();
  const [openShifts, setOpenShifts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [importExcelOpen, setImportExcelOpen] = useState(false);
  // When importing per event card, we scope the import to that card's open_shift IDs.
  // null = global import (from the page header button).
  const [importTargetGroup, setImportTargetGroup] = useState(null);
  // View Schedule dialog — opens a per-event modal with the full staff x days grid.
  const [scheduleViewGroup, setScheduleViewGroup] = useState(null);
  const [scheduleRefresh, setScheduleRefresh] = useState(0);
  // Per-staff schedule view (inside Manage Staff dialog) — read-only.
  const [staffScheduleView, setStaffScheduleView] = useState({ open: false, staff: null });
  // Replace staff dialog state (opened from Manage Staff list)
  const [replaceStaffDialog, setReplaceStaffDialog] = useState({ open: false, staff: null });
  // Manual clock in/out dialog (admin override) — opened from Assigned Staff list
  const [manualClockDialog, setManualClockDialog] = useState({
    open: false,
    employee: null,
    action: undefined,
    shiftId: null,
  });
  const [editShift, setEditShift] = useState(null);
  const [manageShift, setManageShift] = useState(null);
  const [manageGroup, setManageGroup] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, shift: null });
  const [cancelEventDialog, setCancelEventDialog] = useState({ open: false, group: null });
  // Per-day delete confirmation ({ open, shift, group }). Kept separate from
  // cancelEventDialog so the two prompts read differently.
  const [deleteDayDialog, setDeleteDayDialog] = useState({ open: false, shift: null, group: null });
  const [resetTimesDialog, setResetTimesDialog] = useState({ open: false, group: null });
  const [setTimesDialog, setSetTimesDialog] = useState({ open: false, group: null, start_time: '', end_time: '' });
  const [setTimesSaving, setSetTimesSaving] = useState(false);
  // Per-staff schedule dialog — set hours for one staff across event days.
  // employeeShifts holds the per-day db.shifts rows (id, date, start_time, end_time).
  const [scheduleDialog, setScheduleDialog] = useState({
    open: false,
    staff: null,        // {user_id, user_name, user_role}
    group: null,        // the event group
    employeeShifts: [], // [{id, date, start_time, end_time, status}]
    selectedDates: [],  // dates currently checked in the form
    startTime: '',
    endTime: '',
    saving: false
  });
  // Multi-select mode for bulk deleting duplicate shifts inside the Set Hours
  // dialog. Toggle with the "Bulk Delete" button. When true, a 2nd column of
  // checkboxes appears next to each shift row so admin can pick multiple at
  // once and remove them with a single click.
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Embedded modes — this component also powers the Event Calendar's
  // dedicated pages: /admin/events/create (create form only) and
  // /admin/events/view?name=<event> (single-event management page).
  const pathname = window.location.pathname;
  const isCreatePage = pathname === '/admin/events/create';
  const eventPageName = pathname === '/admin/events/view'
    ? (new URLSearchParams(window.location.search).get('name') || '')
    : '';
  const isEventPage = !!eventPageName;
  const isEmbedded = isCreatePage || isEventPage;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('create') === '1' || isCreatePage) setCreateOpen(true);
    const focus = params.get('focus');
    if (focus) setSearchTerm(focus);
    if (eventPageName) setSearchTerm(eventPageName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all'); // all | labour | security | retail
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  // Edit Day Capacity dialog state — admin adjusts staff slots per day for
  // an existing event without removing assigned staff. capacities maps
  // shift_id → new quantity.
  const [capacityDialog, setCapacityDialog] = useState({ open: false, group: null });
  const [capacityValues, setCapacityValues] = useState({});
  const [capacitySaving, setCapacitySaving] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  // Extra Staff per-event state — tracked by group.key so multiple cards can
  // be expanded with their own search term + selected dates without bleeding.
  const [extraStaffState, setExtraStaffState] = useState({});
  
  const defaultEvent = {
    location: '',
    dress_code: '',
    selectedDates: [],
    // Per-date quantity override map { 'YYYY-MM-DD': N }. Empty / missing
    // dates fall back to the main `quantity` field. Lets admin set a
    // different headcount on individual days of a multi-day event.
    quantity_by_date: {},
    // Times are optional — admin can leave blank and set them per-staff later
    // in Shift Calendar. UI shows "PENDING" everywhere until set.
    start_time: '',
    end_time: '',
    position: '',
    quantity: 1,
    // Per-role headcount breakdown. Total = sum(values) is used as `quantity`.
    staff_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
    // Per-role hourly rate (£/hr) breakdown. When any value > 0, that role's
    // shifts pick up its rate instead of the single event-level `hourly_payment`.
    hourly_rate_breakdown: { sia_door_supervisor: 0, cso_steward: 0, cleaner: 0, supervisor: 0 },
    // ISO 4217 currency for all per-role rates (£/€/$).
    currency: 'GBP',
    hourly_payment: '',
    notes: '',
    sia_required: '',
    restricted_to: [],
    priority_employees: [],
    camping_provided: null,
    accommodation_provided: null,
    accommodation_cost_per_day: '',
    transport_provided: null,
    transport_location: '',
    transport_departure_time: '',
    transport_notes: '',
    accommodation_notes: '',
    // Retail vs Event/Festival — drives Shift Calendar grouping.
    shift_category: 'event',
    // Geofenced clock-in: Google Maps / what3words link + radius (meters).
    location_link: '',
    geo_radius: '',
    // 2026-02 — Direct staff assignment at creation time. These employees
    // are added to EVERY day of this event without going through the
    // claim-request approval queue.
    assign_now_ids: [],
  };

  // Multi-event state
  const [eventForms, setEventForms] = useState([{ ...defaultEvent }]);
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [eventMapOpen, setEventMapOpen] = useState(false);
  
  // Alias for backward compat with calendar/edit logic
  const shiftForm = eventForms[activeEventIndex] || defaultEvent;
  const setShiftForm = (updater) => {
    setEventForms(prev => {
      const copy = [...prev];
      if (typeof updater === 'function') {
        copy[activeEventIndex] = updater(copy[activeEventIndex]);
      } else {
        copy[activeEventIndex] = updater;
      }
      return copy;
    });
  };

  // Form state for edit dialog only
  const [shiftFormEdit, setShiftFormEdit] = useState({
    location: '',
    dress_code: '',
    selectedDates: [],
    start_time: '09:00',
    end_time: '17:00',
    position: '',
    quantity: 1,
    hourly_payment: '',
    notes: '',
    sia_required: '',
    restricted_to: [],
    priority_employees: [],
    camping_provided: null,
    accommodation_provided: null,
    accommodation_cost_per_day: '',
    transport_provided: null,
    transport_location: '',
    transport_departure_time: '',
    transport_notes: '',
    accommodation_notes: ''
  });

  // Batch edit group state
  const [batchEditGroup, setBatchEditGroup] = useState(null);
  // Per-event-day hours summary (scheduled vs worked) keyed by open_shift id.
  const [hoursSummary, setHoursSummary] = useState({});
  // Admin Shift Time Adjustment — holds the open dialog state. `candidates`
  // is the list of assigned employees for that day (fetched on open) so
  // admin can uncheck people or edit just one via the shared dialog.
  const [adjustDialog, setAdjustDialog] = useState({
    open: false, openShiftId: null, date: null, eventName: '', candidates: [],
  });
  const [batchEditForm, setBatchEditForm] = useState(null);
  const [batchEditDates, setBatchEditDates] = useState([]);
  const [batchEditOriginalDates, setBatchEditOriginalDates] = useState([]);
  const [batchEditMonth, setBatchEditMonth] = useState(new Date());

  const fetchOpenShifts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/open-shifts`);
      setOpenShifts(response.data);
      // Non-blocking: scheduled vs worked hours per event day (invoicing) —
      // clock-derived, so Operations never fetches it.
      if (!opsMode) {
        axios.get(`${API}/admin/events/hours-summary`)
          .then((r) => setHoursSummary(r.data?.summaries || {}))
          .catch(() => {});
      }
    } catch (error) {
      console.error('Failed to fetch open shifts:', error);
      toast.error('Failed to load upcoming shifts');
    } finally {
      setLoading(false);
    }
  }, []);

  // Open the Admin Time Adjustment dialog for a specific event day. Loads
  // the currently-assigned employees (real shifts, not the open_shift
  // template) so admin can pick which people the correction applies to.
  const openAdjustDialog = useCallback(async (openShift) => {
    if (!openShift) return;
    try {
      const res = await axios.get(`${API}/shifts`, {
        params: { from_open_shift: openShift.id, date: openShift.date }
      });
      // Filter down to shifts that actually match this event day. The
      // /api/shifts endpoint may return other days if the query params
      // aren't respected, so we belt-and-brace filter client-side too.
      const rows = (res.data || []).filter(
        (s) => s.from_open_shift === openShift.id && s.date === openShift.date
      );
      setAdjustDialog({
        open: true,
        openShiftId: openShift.id,
        date: openShift.date,
        eventName: `${openShift.position || ''} @ ${openShift.location || ''}`.trim(),
        candidates: rows,
      });
    } catch (err) {
      toast.error('Failed to load assigned employees for this day');
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const [empRes, supRes] = await Promise.all([
        axios.get(`${API}${opsMode ? '/ops/employees' : '/admin/employees'}`),
        axios.get(`${API}/admin/supervisors`)
      ]);
      setAllUsers([...empRes.data, ...supRes.data]);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  useEffect(() => {
    // Run dedupe sync first so duplicates are cleaned before we render the
    // event cards. Then fetch fresh data. We don't await sync — it can run
    // concurrently; the small delay before refetch is acceptable.
    (async () => {
      try {
        await axios.post(`${API}/admin/sync-shifts`);
      } catch (_e) { /* best-effort */ }
      fetchOpenShifts();
    })();
    fetchAllUsers();
  }, [fetchOpenShifts, fetchAllUsers]);

  // Events view: silent background sync between main admin, secondary admins.
  useAutoRefresh(fetchOpenShifts);

  const handleDateToggle = (dateStr) => {
    setShiftForm(prev => {
      const dates = prev.selectedDates.includes(dateStr)
        ? prev.selectedDates.filter(d => d !== dateStr)
        : [...prev.selectedDates, dateStr];
      return { ...prev, selectedDates: dates };
    });
  };

  // Generate calendar days for a month
  const getMonthDays = (date) => {
    const firstDay = startOfMonth(date);
    const lastDay = endOfMonth(date);
    const days = eachDayOfInterval({ start: firstDay, end: lastDay });
    const startPadding = getDay(firstDay);
    
    return {
      monthLabel: format(date, 'MMMM yyyy'),
      days: days.map(d => ({
        date: d,
        dateStr: format(d, 'yyyy-MM-dd'),
        dayNum: format(d, 'd'),
        isToday: format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
      })),
      startPadding
    };
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    
    // Validate all events
    for (let i = 0; i < eventForms.length; i++) {
      if (eventForms[i].selectedDates.length === 0) {
        toast.error(`Event ${i + 1}: Please select at least one date`);
        setActiveEventIndex(i);
        return;
      }
      // Staff Requirements is an OPTIONAL planning tool. Admin can leave every
      // role at 0 and the event will still save (capacity defaults to 1 slot
      // per day on the backend so the event exists but isn't over-provisioned).
    }
    
    setCreating(true);
    
    try {
      const events = eventForms.map(form => {
        const bd = {
          sia_door_supervisor: parseInt(form.staff_breakdown?.sia_door_supervisor) || 0,
          cso_steward: parseInt(form.staff_breakdown?.cso_steward) || 0,
          cleaner: parseInt(form.staff_breakdown?.cleaner) || 0,
          supervisor: parseInt(form.staff_breakdown?.supervisor) || 0,
        };
        // Fold in headcount for every CUSTOM role slug from the roster so
        // admin can staff their new "Site Manager" etc. right away.
        for (const r of activeRoles) {
          if (!(r.slug in bd)) {
            bd[r.slug] = parseInt(form.staff_breakdown?.[r.slug]) || 0;
          }
        }
        const total = Object.values(bd).reduce((s, v) => s + (v || 0), 0);
        // Per-role hourly rate — always sent so admin can save "0" (inherit
        // single-rate) explicitly. Backend strips all-zero and legacy events
        // continue to use the event-level `hourly_payment`. When the admin
        // left a role's rate at 0 but the role has a `default_hourly_rate`,
        // fall back to that default so the "auto-populate on selection"
        // contract holds without the admin having to retype it.
        const rateBd = {};
        for (const r of activeRoles) {
          const typed = parseFloat(form.hourly_rate_breakdown?.[r.slug]);
          rateBd[r.slug] = (Number.isFinite(typed) && typed > 0)
            ? typed
            : Math.max(0, Number(r.default_hourly_rate) || 0);
        }
        // Ensure the 4 seed slugs are always present even if archived from
        // the roster — legacy consumers read them by name.
        for (const k of ['sia_door_supervisor', 'cso_steward', 'cleaner', 'supervisor']) {
          if (!(k in rateBd)) {
            rateBd[k] = parseFloat(form.hourly_rate_breakdown?.[k]) || 0;
          }
        }
        return {
        location: form.location || '',
        dress_code: form.dress_code || '',
        dates: form.selectedDates.sort(),
        // Pass empty string when admin didn't set times — backend stores
        // "" and the UI shows "PENDING" everywhere.
        start_time: form.start_time || '',
        end_time: form.end_time || '',
        position: form.position || '',
        // When admin filled in Staff Requirements, use its total. Otherwise
        // fall back to 1 slot per day so the event still saves as a bare
        // planning entry (Staff Requirements is optional).
        quantity: total > 0 ? total : 1,
        staff_breakdown: total > 0 ? bd : null,
        hourly_rate_breakdown: rateBd,
        currency: form.currency || 'GBP',
        // Send the per-date map (only keep entries that differ from default
        // and are valid positive ints — keeps payload tidy).
        quantity_by_date: Object.fromEntries(
          Object.entries(form.quantity_by_date || {})
            .map(([d, v]) => [d, parseInt(v)])
            .filter(([d, v]) => Number.isFinite(v) && v > 0 && form.selectedDates.includes(d))
        ),
        hourly_payment: form.hourly_payment ? parseFloat(form.hourly_payment) : null,
        notes: form.notes || '',
        sia_required: form.sia_required || null,
        restricted_to: form.restricted_to.length > 0 ? form.restricted_to : null,
        priority_employees: form.priority_employees.length > 0 ? form.priority_employees : null,
        camping_provided: form.camping_provided,
        accommodation_provided: form.accommodation_provided,
        accommodation_cost_per_day: form.accommodation_provided && form.accommodation_cost_per_day ? parseFloat(form.accommodation_cost_per_day) : null,
        transport_provided: form.transport_provided,
        transport_location: form.transport_provided ? form.transport_location : null,
        transport_departure_time: form.transport_provided ? form.transport_departure_time : null,
        transport_notes: form.transport_notes || null,
        accommodation_notes: form.accommodation_notes || null,
        shift_category: form.shift_category || 'event',
        location_link: form.location_link || null,
        geo_radius: form.location_link && form.geo_radius ? parseInt(form.geo_radius) : null
      };
      });
      
      const response = await axios.post(`${API}/open-shifts/batch`, { events });

      // 2026-02 — When admin picked employees in "Assign staff now",
      // add them to EVERY day of each newly created event immediately,
      // bypassing the claim-request queue.
      try {
        const created = response.data?.created_shifts || response.data?.open_shifts || [];
        // Group created shifts by (location + position) so we can match
        // each eventForm to the shift ids that came out of it.
        for (let idx = 0; idx < eventForms.length; idx++) {
          const form = eventForms[idx];
          const ids = (form.assign_now_ids || []).filter(Boolean);
          if (!ids.length) continue;
          const matching = created
            .filter((cs) => (cs.location || '') === (form.location || '')
                          && (cs.position || '') === (form.position || ''))
            .map((cs) => cs.id);
          if (!matching.length) continue;
          for (const userId of ids) {
            try {
              await axios.post(`${API}/open-shifts/bulk-add-staff`, {
                user_id: userId,
                shift_ids: matching,
              });
            } catch (assignErr) {
              // Non-blocking: keep going even if one user overlaps or fails.
              console.warn('assign-now failed for user', userId, assignErr);
            }
          }
        }
      } catch (bulkErr) {
        console.warn('assign-now bulk stage failed', bulkErr);
      }

      toast.success(response.data.message);
      setCreateOpen(false);
      resetForm();
      fetchOpenShifts();
      if (isCreatePage || new URLSearchParams(window.location.search).get('return') === 'calendar') {
        navigate(opsMode ? '/employee/ops/events' : '/admin/shifts/events');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shifts');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!editShift) return;
    
    setCreating(true);
    
    try {
      const bd = {
        sia_door_supervisor: parseInt(shiftForm.staff_breakdown?.sia_door_supervisor) || 0,
        cso_steward: parseInt(shiftForm.staff_breakdown?.cso_steward) || 0,
        cleaner: parseInt(shiftForm.staff_breakdown?.cleaner) || 0,
        supervisor: parseInt(shiftForm.staff_breakdown?.supervisor) || 0,
      };
      for (const r of activeRoles) {
        if (!(r.slug in bd)) bd[r.slug] = parseInt(shiftForm.staff_breakdown?.[r.slug]) || 0;
      }
      const bdTotal = Object.values(bd).reduce((s, v) => s + (v || 0), 0);
      // Per-role rate — always send (backend clears when all-zero). Missing
      // rates fall back to the role's `default_hourly_rate`.
      const rateBd = {};
      for (const r of activeRoles) {
        const typed = parseFloat(shiftForm.hourly_rate_breakdown?.[r.slug]);
        rateBd[r.slug] = (Number.isFinite(typed) && typed > 0)
          ? typed
          : Math.max(0, Number(r.default_hourly_rate) || 0);
      }
      for (const k of ['sia_door_supervisor', 'cso_steward', 'cleaner', 'supervisor']) {
        if (!(k in rateBd)) rateBd[k] = parseFloat(shiftForm.hourly_rate_breakdown?.[k]) || 0;
      }
      const payload = {
        location: shiftForm.location || null,
        dress_code: shiftForm.dress_code || null,
        date: shiftForm.selectedDates[0] || null,
        start_time: shiftForm.start_time || null,
        end_time: shiftForm.end_time || null,
        position: shiftForm.position || null,
        // When breakdown is set (any bucket > 0), backend derives quantity
        // from its sum. Otherwise fall back to the legacy quantity field so
        // edits on old events don't reset capacity.
        quantity: bdTotal > 0 ? bdTotal : (shiftForm.quantity ? parseInt(shiftForm.quantity) : null),
        staff_breakdown: bdTotal > 0 ? bd : null,
        hourly_rate_breakdown: rateBd,
        currency: shiftForm.currency || 'GBP',
        hourly_payment: shiftForm.hourly_payment ? parseFloat(shiftForm.hourly_payment) : null,
        notes: shiftForm.notes || null,
        sia_required: shiftForm.sia_required || null,
        camping_provided: shiftForm.camping_provided,
        accommodation_provided: shiftForm.accommodation_provided,
        accommodation_cost_per_day: shiftForm.accommodation_provided && shiftForm.accommodation_cost_per_day ? parseFloat(shiftForm.accommodation_cost_per_day) : null,
        transport_provided: shiftForm.transport_provided,
        transport_location: shiftForm.transport_provided ? shiftForm.transport_location : null,
        transport_departure_time: shiftForm.transport_provided ? shiftForm.transport_departure_time : null,
        transport_notes: shiftForm.transport_notes || null,
        accommodation_notes: shiftForm.accommodation_notes || null,
        shift_category: shiftForm.shift_category || 'event'
      };
      
      await axios.put(`${API}/open-shifts/${editShift.id}`, payload);
      toast.success('Shift updated successfully');
      setEditShift(null);
      resetForm();
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setEventForms([{ ...defaultEvent }]);
    setActiveEventIndex(0);
    setCurrentMonth(new Date());
  };

  const openEditDialog = (shift) => {
    // For editing, set a single event form
    setEventForms([{
      location: shift.location || '',
      dress_code: shift.dress_code || '',
      selectedDates: [shift.date],
      start_time: shift.start_time || '09:00',
      end_time: shift.end_time || '17:00',
      position: shift.position || '',
      quantity: shift.quantity || 1,
      staff_breakdown: {
        // Preserve any custom-role slugs on the shift so edits don't drop
        // headcount for non-seed roles ("Site Manager" etc).
        ...(shift.staff_breakdown || {}),
        sia_door_supervisor: Number(shift.staff_breakdown?.sia_door_supervisor) || 0,
        cso_steward: Number(shift.staff_breakdown?.cso_steward) || 0,
        cleaner: Number(shift.staff_breakdown?.cleaner) || 0,
        supervisor: Number(shift.staff_breakdown?.supervisor) || 0,
      },
      hourly_rate_breakdown: {
        ...(shift.hourly_rate_breakdown || {}),
        sia_door_supervisor: Number(shift.hourly_rate_breakdown?.sia_door_supervisor) || 0,
        cso_steward: Number(shift.hourly_rate_breakdown?.cso_steward) || 0,
        cleaner: Number(shift.hourly_rate_breakdown?.cleaner) || 0,
        supervisor: Number(shift.hourly_rate_breakdown?.supervisor) || 0,
      },
      currency: shift.currency || 'GBP',
      hourly_payment: shift.hourly_payment || '',
      notes: shift.notes || '',
      sia_required: shift.sia_required || '',
      restricted_to: shift.restricted_to || [],
      priority_employees: shift.priority_employees || [],
      camping_provided: shift.camping_provided,
      accommodation_provided: shift.accommodation_provided,
      accommodation_cost_per_day: shift.accommodation_cost_per_day || '',
      transport_provided: shift.transport_provided,
      transport_location: shift.transport_location || '',
      transport_departure_time: shift.transport_departure_time || '',
      transport_notes: shift.transport_notes || '',
      accommodation_notes: shift.accommodation_notes || '',
      shift_category: shift.shift_category || 'event'
    }]);
    setActiveEventIndex(0);
    setCurrentMonth(parseISO(shift.date));
    setEditShift(shift);
  };

  const addEvent = () => {
    setEventForms(prev => [...prev, { ...defaultEvent }]);
    setActiveEventIndex(eventForms.length);
  };

  const removeEvent = (index) => {
    if (eventForms.length <= 1) return;
    setEventForms(prev => prev.filter((_, i) => i !== index));
    setActiveEventIndex(prev => Math.min(prev, eventForms.length - 2));
  };

  const handleDeleteShift = async () => {
    if (!deleteDialog.shift) return;
    
    try {
      if (opsMode) {
        await axios.post(`${API}/ops/deletion-requests`, { target_type: 'open_shift', target_id: deleteDialog.shift.id });
        toast.success('Deletion request sent to the Primary Admin for approval');
        setDeleteDialog({ open: false, shift: null });
        return;
      }
      await axios.delete(`${API}/open-shifts/${deleteDialog.shift.id}?force=true`);
      toast.success('Shift deleted successfully');
      setDeleteDialog({ open: false, shift: null });
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const openCapacityDialog = (group) => {
    const initial = {};
    (group.shifts || []).forEach((s) => { initial[s.id] = s.quantity || 1; });
    setCapacityValues(initial);
    setCapacityDialog({ open: true, group });
  };

  const handleSaveCapacity = async () => {
    const group = capacityDialog.group;
    if (!group) return;

    // Build list of shifts whose quantity actually changed.
    const changes = [];
    for (const s of group.shifts) {
      const next = parseInt(capacityValues[s.id], 10);
      if (Number.isNaN(next) || next < 1) {
        toast.error(`Invalid capacity for ${s.date}`);
        return;
      }
      const assigned = (s.claimed_by_list || []).length;
      if (next !== (s.quantity || 1)) changes.push({ id: s.id, quantity: next });
    }

    if (changes.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setCapacitySaving(true);
    try {
      for (const c of changes) {
        await axios.post(`${API}/open-shifts/${c.id}/edit-day-capacity`, { quantity: c.quantity });
      }
      toast.success(`Updated capacity for ${changes.length} day${changes.length !== 1 ? 's' : ''}`);
      setCapacityDialog({ open: false, group: null });
      setCapacityValues({});
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update capacity');
    } finally {
      setCapacitySaving(false);
    }
  };

  const handleClaimShift = async () => {
    // Retired 2026-02 — claim functionality removed. Kept as a noop to
    // avoid accidental invocation from stale UI branches.
    toast.error('Shift claim has been removed. Contact Admin via WhatsApp.');
  };

  const handleCopyEvent = async (group) => {
    // Duplicate every open_shift belonging to this group forward by 7 days.
    const ids = (group.shifts || []).map((s) => s.from_open_shift || s.id).filter(Boolean);
    const uniqueIds = [...new Set(ids)];
    if (!uniqueIds.length) {
      toast.error('Cannot copy — no linked events found for this group.');
      return;
    }
    if (!window.confirm(`Copy "${group.location}" (${uniqueIds.length} day${uniqueIds.length > 1 ? 's' : ''}) to NEXT WEEK? This creates a brand-new event 7 days later with empty staff slots.`)) return;
    try {
      const r = await axios.post(`${API}/admin/open-shifts/copy-event`, {
        open_shift_ids: uniqueIds,
        days_offset: 7,
      });
      toast.success(r.data?.message || 'Event duplicated to next week');
      fetchOpenShifts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copy failed');
    }
  };

  const handleRemoveStaff = async (shiftId, userId) => {
    try {
      const res = await axios.post(`${API}/open-shifts/${shiftId}/remove-staff?user_id=${userId}`);
      toast.success(res.data.message || 'Staff removed');
      fetchOpenShifts();
      const updated = await axios.get(`${API}/open-shifts/${shiftId}`);
      setManageShift(updated.data);
      if (window.confirm('Staff removed. Email available staff about the freed spot(s) now?')) {
        const ids = manageGroup?.shifts?.length ? manageGroup.shifts.map((s) => s.id) : [shiftId];
        try {
          const r = await axios.post(`${API}/open-shifts/email-available-staff`, { shift_ids: ids });
          toast.success(r.data.message);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to send emails');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove staff');
    }
  };

  // Add staff to ONLY the currently displayed date in the Manage dialog.
  // For multi-day events, admins can add the same person to other dates
  // using the day-chips on the expanded event card, or via the
  // "Add to All Days" button shown below the search.
  const askNotify = () => window.confirm('Send email notification to the affected staff?\n\nOK = send email now\nCancel = save WITHOUT sending any email');

  const handleAddStaff = async (shiftId, userId) => {
    try {
      const notify = askNotify();
      await axios.post(`${API}/open-shifts/${shiftId}/add-staff?notify=${notify}`, { user_id: userId });
      toast.success(`Staff added to this day${notify ? ' — email sent' : ' (no email sent)'}`);
      fetchOpenShifts();
      const updated = await axios.get(`${API}/open-shifts/${shiftId}`);
      setManageShift(updated.data);
      setStaffSearchTerm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  // Explicit bulk action: add staff to every day of the multi-day event.
  // Skipped days (already on / full / overlapping) are reported back so admin
  // sees exactly what happened — no silent surprises.
  const handleAddStaffToAllDays = async (userId) => {
    if (!manageGroup || manageGroup.shifts.length <= 1) return;
    const allShiftIds = manageGroup.shifts.map(s => s.id);
    try {
      const notify = askNotify();
      const res = await axios.post(`${API}/open-shifts/bulk-add-staff?notify=${notify}`, { user_id: userId, shift_ids: allShiftIds });
      const { added = 0, skipped = 0 } = res.data || {};
      if (added > 0) {
        toast.success(`Added to ${added} day${added !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped — already booked or full)` : ''}`);
      } else {
        toast.info('Already assigned to all available days');
      }
      fetchOpenShifts();
      if (manageShift) {
        const updated = await axios.get(`${API}/open-shifts/${manageShift.id}`);
        setManageShift(updated.data);
      }
      setStaffSearchTerm('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  const handleSendReminder = async (shiftId) => {
    try {
      const response = await axios.post(`${API}/open-shifts/${shiftId}/send-reminder`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleDownloadSchedule = async (group) => {
    const location = group.shifts[0]?.location;
    const openShiftIds = group.shifts.map((s) => s.id).filter(Boolean).join(',');
    if (!location && !openShiftIds) return;
    try {
      const res = await axios.get(`${API}/admin/events/rota/schedule-export`, {
        params: { location, open_shift_ids: openShiftIds }, responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : `${location}_schedule.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Schedule downloaded');
    } catch (err) {
      if (err.response?.status === 404) toast.error('No shifts scheduled from tomorrow onwards for this event');
      else toast.error('Failed to download schedule');
    }
  };

  const handleEmailAvailableStaff = async (group) => {
    const shiftIds = group.shifts.map(s => s.id);
    try {
      const response = await axios.post(`${API}/open-shifts/email-available-staff`, { shift_ids: shiftIds });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    }
  };

  const handleEmailStaffSchedule = async (group) => {
    const shiftIds = group.shifts.map(s => s.id);
    const label = group.shifts[0]?.position || group.shifts[0]?.location || 'this event';
    if (!window.confirm(`Send every assigned staff member their current schedule for ${label} by email now?`)) return;
    try {
      const res = await axios.post(`${API}/admin/event-schedule/send-emails`, { open_shift_ids: shiftIds });
      toast.success(`Schedule emailed to ${res.data.emails_sent} of ${res.data.staff_count} staff`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send schedule emails');
    }
  };

  const handleCancelEvent = async () => {
    const group = cancelEventDialog.group;
    if (!group) return;
    setCreating(true);
    try {
      const openShiftIds = (group.shifts || []).map((s) => s.id).filter(Boolean);
      if (opsMode) {
        await axios.post(`${API}/ops/deletion-requests`, {
          target_type: 'open_shift', target_id: openShiftIds[0], group_ids: openShiftIds,
        });
        toast.success('Full-event deletion request sent to the Primary Admin for approval');
        setCancelEventDialog({ open: false, group: null });
        return;
      }
      // New cascade endpoint: deletes every day of the event in one call, keeps
      // already-worked shifts intact for history, and writes ONE audit-log
      // entry (instead of one per day like the old per-shift DELETE loop).
      const res = await axios.post(`${API}/admin/open-shifts/delete-full-event`, {
        open_shift_ids: openShiftIds,
      });
      const d = res.data || {};
      const preservedNote = d.preserved_shifts
        ? ` (${d.preserved_shifts} completed shift(s) preserved)`
        : '';
      toast.success(
        `Event deleted — ${d.deleted_shifts || 0} shift(s) across ${d.deleted_days || openShiftIds.length} day(s)${preservedNote}`
      );
      setCancelEventDialog({ open: false, group: null });
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEventDay = async () => {
    const shift = deleteDayDialog.shift;
    if (!shift) return;
    setCreating(true);
    try {
      if (opsMode) {
        await axios.post(`${API}/ops/deletion-requests`, { target_type: 'open_shift', target_id: shift.id });
        toast.success('Day deletion request sent to the Primary Admin for approval');
        setDeleteDayDialog({ open: false, shift: null, group: null });
        return;
      }
      const res = await axios.delete(`${API}/admin/open-shifts/${shift.id}/day`);
      const d = res.data || {};
      const preservedNote = d.preserved_shifts
        ? ` (${d.preserved_shifts} completed shift(s) preserved)`
        : '';
      toast.success(
        `Day deleted — ${d.deleted_shifts || 0} shift(s) removed${preservedNote}`
      );
      setDeleteDayDialog({ open: false, shift: null, group: null });
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event day');
    } finally {
      setCreating(false);
    }
  };

  const handleBroadcast = async (shiftId) => {
    try {
      const response = await axios.post(`${API}/open-shifts/${shiftId}/broadcast`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to broadcast');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/open-shifts/export`);
      const data = response.data.shifts;
      
      const headers = ['Date', 'Position', 'Location', 'Start Time', 'End Time', 'Dress Code', 'Staff Needed', 'Staff Assigned', 'Assigned Staff', 'Hourly Rate', 'Status', 'SIA Required', 'Camping', 'Accommodation', 'Accommodation Cost', 'Transport', 'Transport Location', 'Transport Time', 'Notes'];
      const csvRows = [headers.join(',')];
      
      data.forEach(row => {
        const values = [
          row.date,
          `"${row.position}"`,
          `"${row.location}"`,
          row.start_time,
          row.end_time,
          `"${row.dress_code}"`,
          row.staff_needed,
          row.staff_assigned,
          `"${row.assigned_staff}"`,
          row.hourly_rate || '',
          row.status,
          row.sia_required || '',
          row.camping_provided ? 'Yes' : 'No',
          row.accommodation_provided ? 'Yes' : 'No',
          row.accommodation_cost_per_day || '',
          row.transport_provided ? 'Yes' : 'No',
          `"${row.transport_location || ''}"`,
          row.transport_departure_time || '',
          `"${(row.notes || '').replace(/"/g, '""')}"`
        ];
        csvRows.push(values.join(','));
      });
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shifts_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} shifts`);
    } catch (error) {
      toast.error('Failed to export shifts');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEE, MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleGenerateBadge = async (group) => {
    try {
      const response = await axios.post(`${API}/feedback/generate-badge`, {
        position: group.position,
        location: group.location,
        start_time: group.start_time,
        end_time: group.end_time
      });
      const data = response.data;
      const params = new URLSearchParams({
        code: data.code,
        location: data.location,
        dates: data.dates.join(','),
        start_time: data.start_time,
        end_time: data.end_time
      });
      navigate(`/print-badge?${params.toString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate badge');
    }
  };

  // Open batch edit dialog for entire grouped event
  const openBatchEditDialog = (group) => {
    const dates = [...new Set(group.shifts.map(s => s.date))].sort();
    setBatchEditGroup(group);
    setBatchEditDates([...dates]);
    setBatchEditOriginalDates([...dates]);
    setBatchEditMonth(dates.length > 0 ? parseISO(dates[0]) : new Date());
    setBatchEditForm({
      location: group.location || '',
      dress_code: group.dress_code || '',
      start_time: group.start_time || '09:00',
      end_time: group.end_time || '17:00',
      position: group.position || '',
      quantity: group.shifts[0]?.quantity || 1,
      staff_breakdown: {
        // Spread first so custom-role slugs survive edits.
        ...(group.shifts[0]?.staff_breakdown || {}),
        sia_door_supervisor: Number(group.shifts[0]?.staff_breakdown?.sia_door_supervisor) || 0,
        cso_steward: Number(group.shifts[0]?.staff_breakdown?.cso_steward) || 0,
        cleaner: Number(group.shifts[0]?.staff_breakdown?.cleaner) || 0,
        supervisor: Number(group.shifts[0]?.staff_breakdown?.supervisor) || 0,
      },
      hourly_rate_breakdown: {
        ...(group.shifts[0]?.hourly_rate_breakdown || {}),
        sia_door_supervisor: Number(group.shifts[0]?.hourly_rate_breakdown?.sia_door_supervisor) || 0,
        cso_steward: Number(group.shifts[0]?.hourly_rate_breakdown?.cso_steward) || 0,
        cleaner: Number(group.shifts[0]?.hourly_rate_breakdown?.cleaner) || 0,
        supervisor: Number(group.shifts[0]?.hourly_rate_breakdown?.supervisor) || 0,
      },
      currency: group.shifts[0]?.currency || 'GBP',
      hourly_payment: group.hourly_payment || '',
      notes: group.notes || '',
      sia_required: group.sia_required || '',
      camping_provided: group.camping_provided,
      accommodation_provided: group.accommodation_provided,
      accommodation_cost_per_day: group.accommodation_cost_per_day || '',
      transport_provided: group.transport_provided,
      transport_location: group.transport_location || '',
      transport_departure_time: group.transport_departure_time || '',
      transport_notes: group.transport_notes || '',
      accommodation_notes: group.accommodation_notes || '',
      shift_category: group.shift_category || 'event'
    });
  };

  const handleBatchUpdate = async (e) => {
    e.preventDefault();
    if (!batchEditGroup || !batchEditForm) return;
    if (batchEditDates.length === 0) {
      toast.error('Event must have at least one date');
      return;
    }
    setCreating(true);
    try {
      // Compute breakdown & derived total once for the whole batch update.
      const bd = {
        sia_door_supervisor: parseInt(batchEditForm.staff_breakdown?.sia_door_supervisor) || 0,
        cso_steward: parseInt(batchEditForm.staff_breakdown?.cso_steward) || 0,
        cleaner: parseInt(batchEditForm.staff_breakdown?.cleaner) || 0,
        supervisor: parseInt(batchEditForm.staff_breakdown?.supervisor) || 0,
      };
      for (const r of activeRoles) {
        if (!(r.slug in bd)) bd[r.slug] = parseInt(batchEditForm.staff_breakdown?.[r.slug]) || 0;
      }
      const bdTotal = Object.values(bd).reduce((s, v) => s + (v || 0), 0);
      // Per-role rate breakdown for the whole batch.
      const rateBd = {};
      for (const r of activeRoles) {
        const typed = parseFloat(batchEditForm.hourly_rate_breakdown?.[r.slug]);
        rateBd[r.slug] = (Number.isFinite(typed) && typed > 0)
          ? typed
          : Math.max(0, Number(r.default_hourly_rate) || 0);
      }
      for (const k of ['sia_door_supervisor', 'cso_steward', 'cleaner', 'supervisor']) {
        if (!(k in rateBd)) rateBd[k] = parseFloat(batchEditForm.hourly_rate_breakdown?.[k]) || 0;
      }
      // 1. Find dates to add and remove
      const addedDates = batchEditDates.filter(d => !batchEditOriginalDates.includes(d));
      const removedDates = batchEditOriginalDates.filter(d => !batchEditDates.includes(d));
      const remainingDates = batchEditDates.filter(d => batchEditOriginalDates.includes(d));

      // 2. Delete removed dates (all shifts for each removed date)
      for (const dateStr of removedDates) {
        const shiftsForDate = batchEditGroup.shifts.filter(s => s.date === dateStr);
        for (const shift of shiftsForDate) {
          try {
            await axios.delete(`${API}/open-shifts/${shift.id}?force=true`);
          } catch (err) {
            console.warn(`Could not delete shift for ${dateStr}:`, err.response?.data?.detail);
          }
        }
      }

      // 3. Create new dates (using existing single create endpoint). When the
      //    event is being extended (existing days remain), inherit staff from
      //    the first surviving day so admin doesn't have to re-add 12 people
      //    to each new date manually.
      if (addedDates.length > 0) {
        // Pick a remaining (still-existing) source open_shift to copy staff from.
        const sourceShift = batchEditGroup.shifts.find(
          (s) => remainingDates.includes(s.date) && (s.claimed_count || 0) > 0
        );
        await axios.post(`${API}/open-shifts`, {
          location: batchEditForm.location || '',
          dress_code: batchEditForm.dress_code || '',
          dates: addedDates,
          start_time: batchEditForm.start_time || '09:00',
          end_time: batchEditForm.end_time || '17:00',
          position: batchEditForm.position || '',
          quantity: bdTotal > 0 ? bdTotal : (parseInt(batchEditForm.quantity) || 1),
          staff_breakdown: bdTotal > 0 ? bd : null,
          hourly_rate_breakdown: rateBd,
          currency: batchEditForm.currency || 'GBP',
          hourly_payment: batchEditForm.hourly_payment ? parseFloat(batchEditForm.hourly_payment) : null,
          notes: batchEditForm.notes || '',
          sia_required: batchEditForm.sia_required || null,
          camping_provided: batchEditForm.camping_provided,
          accommodation_provided: batchEditForm.accommodation_provided,
          accommodation_cost_per_day: batchEditForm.accommodation_provided && batchEditForm.accommodation_cost_per_day ? parseFloat(batchEditForm.accommodation_cost_per_day) : null,
          transport_provided: batchEditForm.transport_provided,
          transport_location: batchEditForm.transport_provided ? batchEditForm.transport_location : null,
          transport_departure_time: batchEditForm.transport_provided ? batchEditForm.transport_departure_time : null,
          transport_notes: batchEditForm.transport_notes || null,
          accommodation_notes: batchEditForm.accommodation_notes || null,
          shift_category: batchEditForm.shift_category || 'event',
          inherit_staff_from_open_shift_id: sourceShift?.id || null,
        });
      }

      // 4. Update remaining shifts by their IDs
      if (remainingDates.length > 0) {
        const shiftIdsToUpdate = batchEditGroup.shifts
          .filter(s => remainingDates.includes(s.date))
          .map(s => s.id);
        
        const updateData = {
          location: batchEditForm.location || null,
          dress_code: batchEditForm.dress_code || null,
          start_time: batchEditForm.start_time || null,
          end_time: batchEditForm.end_time || null,
          position: batchEditForm.position || null,
          quantity: bdTotal > 0 ? bdTotal : (batchEditForm.quantity ? parseInt(batchEditForm.quantity) : null),
          staff_breakdown: bdTotal > 0 ? bd : null,
          hourly_rate_breakdown: rateBd,
          currency: batchEditForm.currency || 'GBP',
          hourly_payment: batchEditForm.hourly_payment ? parseFloat(batchEditForm.hourly_payment) : null,
          notes: batchEditForm.notes || null,
          sia_required: batchEditForm.sia_required || null,
          camping_provided: batchEditForm.camping_provided,
          accommodation_provided: batchEditForm.accommodation_provided,
          accommodation_cost_per_day: batchEditForm.accommodation_provided && batchEditForm.accommodation_cost_per_day ? parseFloat(batchEditForm.accommodation_cost_per_day) : null,
          transport_provided: batchEditForm.transport_provided,
          transport_location: batchEditForm.transport_provided ? batchEditForm.transport_location : null,
          transport_departure_time: batchEditForm.transport_provided ? batchEditForm.transport_departure_time : null,
          transport_notes: batchEditForm.transport_notes || null,
          accommodation_notes: batchEditForm.accommodation_notes || null,
          shift_category: batchEditForm.shift_category || 'event'
        };

        await axios.put(`${API}/open-shifts/batch-update-by-ids`, {
          shift_ids: shiftIdsToUpdate,
          update_data: updateData
        });
      }

      const parts = [];
      if (remainingDates.length > 0) parts.push(`Updated ${remainingDates.length} days`);
      if (addedDates.length > 0) parts.push(`Added ${addedDates.length} new days`);
      if (removedDates.length > 0) parts.push(`Removed ${removedDates.length} days`);
      toast.success(parts.join(', ') || 'Event updated');
      
      setBatchEditGroup(null);
      setBatchEditForm(null);
      setBatchEditDates([]);
      setBatchEditOriginalDates([]);
      fetchOpenShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to batch update');
    } finally {
      setCreating(false);
    }
  };

  // Filter shifts
  const filteredShifts = openShifts.filter(shift => {
    // Dedicated event page: exact location match only.
    if (isEventPage) return (shift.location || '') === eventPageName;
    const matchesSearch = 
      shift.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shift.position?.toLowerCase().includes(searchTerm.toLowerCase());
    // Category calendars: client-created shifts carry `category`; anything
    // without one is treated as Security (the app's historical default).
    const shiftCat = shift.category || 'security';
    const matchesCategory = filterCategory === 'all' || shiftCat === filterCategory;
    if (!matchesCategory) return false;
    
    if (filterStatus === 'available') {
      return matchesSearch && shift.status === 'available' && (shift.claimed_count || 0) < (shift.quantity || 1);
    } else if (filterStatus === 'full') {
      return matchesSearch && (shift.status === 'full' || (shift.claimed_count || 0) >= (shift.quantity || 1));
    }
    return matchesSearch;
  });

  const availableShifts = filteredShifts.filter(s => (s.claimed_count || 0) < (s.quantity || 1));
  const fullShifts = filteredShifts.filter(s => (s.claimed_count || 0) >= (s.quantity || 1));

  const filteredUsers = allUsers.filter(u => {
    if (!staffSearchTerm) return true;
    return u.name?.toLowerCase().includes(staffSearchTerm.toLowerCase());
  });

  const hasUserClaimed = (shift) => {
    return shift.claimed_by_list?.some(c => c.user_id === user?.id);
  };

  // Group shifts by position+location into events. We deliberately IGNORE
  // start_time/end_time in the key so that editing a single day's hours
  // does NOT spawn a new event card. The per-day times are still preserved
  // on each shift row and visible in Manage Staff → schedule view.
  // Position + location are normalised (trim + lowercase + collapse spaces)
  // so trivial typos like trailing spaces or "Finsbury park" vs "Finsbury Park"
  // do not split the same event into two cards.
  const normaliseKey = (s) => (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const groupShifts = (shifts) => {
    const groups = {};
    shifts.forEach(shift => {
      const key = `${normaliseKey(shift.position)}|${normaliseKey(shift.location)}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          position: shift.position || 'TBD',
          location: shift.location || 'TBD',
          start_time: shift.start_time || 'TBD',
          end_time: shift.end_time || 'TBD',
          dress_code: shift.dress_code || '',
          hourly_payment: shift.hourly_payment,
          sia_required: shift.sia_required,
          camping_provided: shift.camping_provided,
          accommodation_provided: shift.accommodation_provided,
          accommodation_cost_per_day: shift.accommodation_cost_per_day,
          transport_provided: shift.transport_provided,
          transport_location: shift.transport_location,
          transport_departure_time: shift.transport_departure_time,
          transport_notes: shift.transport_notes,
          accommodation_notes: shift.accommodation_notes,
          shift_category: shift.shift_category || 'event',
          currency: shift.currency || 'GBP',
          source: shift.source,
          company_name: shift.company_name,
          category: shift.category,
          notes: shift.notes,
          shifts: []
        };
      }
      groups[key].shifts.push(shift);
    });
    // Sort each group's shifts by date, then derive a "mixed_times" flag
    // and a representative time range for the card header.
    Object.values(groups).forEach(g => {
      g.shifts.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      const uniqStart = new Set(g.shifts.map(s => s.start_time || ''));
      const uniqEnd = new Set(g.shifts.map(s => s.end_time || ''));
      g.mixed_times = uniqStart.size > 1 || uniqEnd.size > 1;
      // Card header still shows the first shift's time as the default —
      // per-day times remain accurate inside Manage Staff.
    });
    return Object.values(groups);
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Collect all unique employees across a group's shifts + their assigned dates
  const getGroupEmployees = (group) => {
    const map = {};
    group.shifts.forEach(shift => {
      (shift.claimed_by_list || []).forEach(c => {
        if (!map[c.user_id]) map[c.user_id] = { ...c, dates: new Set(), extraDates: new Set() };
        map[c.user_id].dates.add(shift.date);
        if (c.is_extra) map[c.user_id].extraDates.add(shift.date);
      });
    });
    // Convert dates Set → sorted array
    Object.values(map).forEach(e => {
      e.dates = Array.from(e.dates).sort();
      e.extraDates = Array.from(e.extraDates).sort();
    });
    return Object.values(map);
  };

  // Reset start/end times on every day of this event back to "Pending",
  // and reset the linked per-employee shifts that haven't started yet.
  const handleResetEventTimes = async (group) => {
    const shiftIds = group.shifts.map(s => s.id);
    try {
      const res = await axios.post(`${API}/open-shifts/reset-event-times`, { shift_ids: shiftIds });
      toast.success(res.data?.message || 'Times reset to Pending');
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset times');
    }
  };

  const handleSetEventTimes = async () => {
    const { group, start_time, end_time } = setTimesDialog;
    if (!group) return;
    // Validate HH:MM
    const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (!hhmm.test(start_time) || !hhmm.test(end_time)) {
      toast.error('Start and end time must be in HH:MM format (e.g. 18:00)');
      return;
    }
    setSetTimesSaving(true);
    try {
      const notify = askNotify();
      const shiftIds = group.shifts.map(s => s.id);
      const res = await axios.post(`${API}/open-shifts/set-event-times?notify=${notify}`, {
        shift_ids: shiftIds,
        start_time,
        end_time,
      });
      toast.success(res.data?.message || 'Schedule applied to all staff');
      setSetTimesDialog({ open: false, group: null, start_time: '', end_time: '' });
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply schedule');
    } finally {
      setSetTimesSaving(false);
    }
  };

  // Open dialog: fetch this employee's per-day shifts within the event date
  // range so admin can set hours per-day (one-at-a-time or batch).
  // Variant C — we include shifts from OTHER events that fall on the same
  // dates so admin sees the staff's full picture across overlapping events
  // (e.g. the staff is on Card 1 [13,14,15] and Card 2 [14,15]).
  const openScheduleDialogForStaff = async (group, staff) => {
    if (!group || !staff) return;
    const dates = [...new Set(group.shifts.map(s => s.date))].sort();
    if (dates.length === 0) return;
    try {
      const res = await axios.get(`${API}/shifts/calendar`, { params: { start_date: dates[0], end_date: dates[dates.length - 1] } });
      const dateSet = new Set(dates);
      // Include EVERY shift for this employee whose date falls in the event
      // range. Don't restrict to `from_open_shift in group.shifts` — that's
      // the whole point of Variant C: see all overlapping work.
      const employeeShifts = (res.data || [])
        .filter(s => s.employee_id === staff.user_id && dateSet.has(s.date))
        .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || '').localeCompare(b.start_time || ''));
      // Default-pick ONLY the shifts that are PENDING (no times yet) AND not
      // locked (in_progress/completed). This way, when admin opens the dialog
      // after adding ONE new shift for ONE specific day, only that PENDING
      // shift is pre-checked — not the other days that already have times.
      // Admin can still manually check additional shifts if needed.
      const thisEventOpenShiftIds = new Set(group.shifts.map(s => s.id));
      const defaultSelected = employeeShifts
        .filter(s => thisEventOpenShiftIds.has(s.from_open_shift))
        .filter(s => !s.start_time || !s.end_time)
        .filter(s => s.status !== 'in_progress' && s.status !== 'completed')
        .map(s => s.id);
      // Close Manage dialog so the schedule dialog gets focus
      setManageShift(null);
      setManageGroup(null);
      setScheduleDialog({
        open: true,
        staff,
        group,
        employeeShifts,
        // selectedIds now (we toggle by id, not date, so split shifts work)
        selectedDates: defaultSelected,
        startTime: '',
        endTime: '',
        saving: false
      });
    } catch (err) {
      toast.error('Failed to load staff schedule');
    }
  };

  // Toggle by shift ID (works for split shifts and across events)
  const toggleScheduleDate = (shiftId) => {
    setScheduleDialog(prev => ({
      ...prev,
      selectedDates: prev.selectedDates.includes(shiftId)
        ? prev.selectedDates.filter(d => d !== shiftId)
        : [...prev.selectedDates, shiftId]
    }));
  };

  // Reload helper used after Apply/Clear/Split
  const reloadScheduleDialog = async (group, staff) => {
    if (!group || !staff) return;
    const dates = [...new Set(group.shifts.map(s => s.date))].sort();
    if (dates.length === 0) return;
    try {
      const res = await axios.get(`${API}/shifts/calendar`, { params: { start_date: dates[0], end_date: dates[dates.length - 1] } });
      const dateSet = new Set(dates);
      const fresh = (res.data || [])
        .filter(s => s.employee_id === staff.user_id && dateSet.has(s.date))
        .sort((a, b) => a.date.localeCompare(b.date) || (a.start_time || '').localeCompare(b.start_time || ''));
      setScheduleDialog(prev => ({ ...prev, employeeShifts: fresh }));
      setScheduleRefresh((x) => x + 1);
    } catch {}
  };

  // Apply form's start/end to selected shifts via PUT /shifts/{id}.
  const handleApplyScheduleTimes = async () => {
    const { staff, group, employeeShifts, selectedDates, startTime, endTime } = scheduleDialog;
    if (!startTime || !endTime) { toast.error('Set both Start and End time'); return; }
    if (selectedDates.length === 0) { toast.error('Pick at least one shift'); return; }
    setScheduleDialog(prev => ({ ...prev, saving: true }));
    const notifyBulk = askNotify();
    let ok = 0; let fail = 0; const failMsgs = [];
    for (const shiftId of selectedDates) {
      const shift = employeeShifts.find(s => s.id === shiftId);
      if (!shift) { fail++; continue; }
      try {
        await axios.put(`${API}/shifts/${shift.id}?notify=${notifyBulk}`, { start_time: startTime, end_time: endTime });
        ok++;
      } catch (err) {
        fail++;
        if (err.response?.data?.detail) failMsgs.push(err.response.data.detail);
      }
    }
    if (ok > 0) toast.success(`Hours set for ${ok} shift${ok !== 1 ? 's' : ''}${fail ? ` (${fail} failed)` : ''}`);
    if (ok === 0 && fail > 0) toast.error(failMsgs[0] || 'Failed to set hours');
    await reloadScheduleDialog(group, staff);
    setScheduleDialog(prev => ({ ...prev, startTime: '', endTime: '', saving: false, selectedDates: [] }));
    fetchOpenShifts();
  };

  // Clear ONE shift's times → flip back to PENDING.
  const handleClearScheduleDay = async (shift) => {
    try {
      await axios.put(`${API}/shifts/${shift.id}`, { start_time: '', end_time: '' });
      setScheduleDialog(prev => ({
        ...prev,
        employeeShifts: prev.employeeShifts.map(s => s.id === shift.id ? { ...s, start_time: '', end_time: '' } : s)
      }));
      toast.success(`Cleared ${shift.date} → Pending`);
      fetchOpenShifts();
    } catch (err) {
      toast.error('Failed to clear day');
    }
  };

  // Add a sibling "split" shift for the same employee on the same date.
  const handleAddSplit = async (sourceShift) => {
    try {
      await axios.post(`${API}/shifts/${sourceShift.id}/split`);
      toast.success(`Split shift added for ${sourceShift.date}`);
      await reloadScheduleDialog(scheduleDialog.group, scheduleDialog.staff);
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add split');
    }
  };

  // Delete a pending/scheduled shift (only used for splits — protects in_progress).
  const handleDeleteScheduledShift = async (shift) => {
    try {
      if (opsMode) {
        await axios.post(`${API}/ops/deletion-requests`, { target_type: 'shift', target_id: shift.id });
        toast.success('Deletion request sent to the Primary Admin for approval');
        return;
      }
      await axios.delete(`${API}/shifts/${shift.id}`);
      toast.success(shift.is_split ? 'Split removed' : 'Shift deleted');
      await reloadScheduleDialog(scheduleDialog.group, scheduleDialog.staff);
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const handleBulkDeleteShifts = async () => {
    if (opsMode) {
      toast.info('Operations cannot bulk-delete. Use "Request Deletion" on individual shifts instead.');
      return;
    }
    if (bulkDeleteIds.length === 0) {
      toast.error('Select at least one shift to delete');
      return;
    }
    if (!window.confirm(`Delete ${bulkDeleteIds.length} shift(s)? This cannot be undone.`)) return;
    try {
      const res = await axios.post(`${API}/shifts/bulk-delete`, { shift_ids: bulkDeleteIds });
      toast.success(res.data?.message || `${bulkDeleteIds.length} shift(s) deleted`);
      setBulkDeleteIds([]);
      setBulkDeleteMode(false);
      await reloadScheduleDialog(scheduleDialog.group, scheduleDialog.staff);
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const toggleBulkSelect = (shiftId) => {
    setBulkDeleteIds(prev =>
      prev.includes(shiftId)
        ? prev.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  // Add an existing employee to another day of the same event
  const handleAddStaffToDate = async (group, userId, date) => {
    const targetShift = group.shifts.find(s => s.date === date);
    if (!targetShift) return;
    if ((targetShift.claimed_count || 0) >= (targetShift.quantity || 1)) {
      toast.error(`${formatDate(date)} is already fully booked`);
      return;
    }
    try {
      await axios.post(`${API}/open-shifts/${targetShift.id}/add-staff`, { user_id: userId });
      toast.success(`Added to ${formatDate(date)}`);
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add staff');
    }
  };

  // Cleanup any duplicate open_shifts within the same event (same position +
  // location on the same date) and dedupe per-user shift rows. Safe — keeps
  // the earliest record and re-points all data (claims, shifts) onto it.
  const [consolidatingKey, setConsolidatingKey] = useState(null);
  // "Add Days & Staff" dialog — extends an event with new dates and selectively
  // assigns existing event staff to them. Times are optional (Pending Schedule).
  const [addDaysGroup, setAddDaysGroup] = useState(null);
  const [addDaysDates, setAddDaysDates] = useState([]); // ['YYYY-MM-DD']
  const [addDaysCalMonth, setAddDaysCalMonth] = useState(() => new Date());
  const [addDaysStaff, setAddDaysStaff] = useState({}); // { userId: bool }
  const [addDaysTimes, setAddDaysTimes] = useState({}); // { 'userId|date': { start, end } }
  const [addDaysStaffSearch, setAddDaysStaffSearch] = useState('');
  const [addDaysSaving, setAddDaysSaving] = useState(false);
  const handleConsolidateDuplicates = async (group) => {
    if (!group?.shifts?.length) return;
    setConsolidatingKey(group.key);
    try {
      const res = await axios.post(`${API}/admin/events/consolidate-duplicates`, {
        open_shift_ids: group.shifts.map((s) => s.id),
      });
      toast.success(res.data?.message || 'Duplicates consolidated');
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not consolidate duplicates');
    } finally {
      setConsolidatingKey(null);
    }
  };

  // Open the "Add Days & Staff" dialog for a given event card. Pre-selects
  // every existing employee so the common case (extend with all current
  // staff) is one click away.
  const openAddDaysDialog = (group) => {
    setAddDaysGroup(group);
    setAddDaysDates([]);
    setAddDaysStaff({});
    setAddDaysTimes({});
    setAddDaysStaffSearch('');
    // Default the calendar to the event's first scheduled date so the admin
    // sees the right month immediately instead of "today".
    try {
      const dates = (group.shifts || [])
        .map(s => s.date)
        .filter(Boolean)
        .sort();
      const firstDateStr = dates[0];
      setAddDaysCalMonth(firstDateStr ? startOfMonth(parseISO(firstDateStr)) : new Date());
    } catch {
      setAddDaysCalMonth(new Date());
    }
  };

  const closeAddDaysDialog = () => {
    setAddDaysGroup(null);
    setAddDaysDates([]);
    setAddDaysStaff({});
    setAddDaysTimes({});
  };

  // Orchestrates: create open_shift for each new date, claim each picked
  // staff onto it, then optionally apply per-row custom times. Empty times
  // are intentional → shift remains "Pending Schedule" for that user/day.
  const handleSaveAddDays = async () => {
    if (!addDaysGroup) return;
    if (!addDaysDates.length) { toast.error('Pick at least one extra day'); return; }
    const pickedUserIds = Object.entries(addDaysStaff).filter(([, v]) => v).map(([k]) => k);
    if (!pickedUserIds.length) { toast.error('Select at least one staff member'); return; }

    setAddDaysSaving(true);
    try {
      const sourceShift = addDaysGroup.shifts[0];
      for (const date of addDaysDates) {
        // 1) Create the new day's open_shift (no inherit — we add staff one by one to honour the picks).
        //    skip_broadcast prevents the "new available shift" email from going
        //    out to the entire staff pool — only the specifically picked
        //    members below will receive the assignment confirmation email.
        //    quantity = pickedUserIds.length (NOT the source's quantity) so the
        //    new day reaches "full" once every picked member is assigned —
        //    otherwise the event card would flip from green (Full) to blue
        //    (Available) just because admin chose a smaller subset.
        const createRes = await axios.post(`${API}/open-shifts`, {
          location: sourceShift?.location || '',
          dress_code: sourceShift?.dress_code || '',
          dates: [date],
          start_time: sourceShift?.start_time || '',
          end_time: sourceShift?.end_time || '',
          position: sourceShift?.position || '',
          quantity: pickedUserIds.length,
          hourly_payment: sourceShift?.hourly_payment ?? null,
          sia_required: sourceShift?.sia_required ?? null,
          camping_provided: sourceShift?.camping_provided ?? false,
          accommodation_provided: sourceShift?.accommodation_provided ?? false,
          transport_provided: sourceShift?.transport_provided ?? false,
          skip_broadcast: true,
        });
        const newOpenShift = (createRes.data?.shifts || createRes.data || [])[0] || createRes.data;
        const newOpenShiftId = newOpenShift?.id;
        if (!newOpenShiftId) continue;

        // 2) Assign every picked staff onto the new day.
        for (const uid of pickedUserIds) {
          try {
            await axios.post(`${API}/open-shifts/${newOpenShiftId}/add-staff`, { user_id: uid });
          } catch (e) {
            // Skip silently — most likely already added or capacity reached
          }
        }

        // 3) Apply per-staff custom times when admin entered them. Empty
        //    times → shift stays at the open_shift default (which may itself
        //    be empty → renders as "Pending Schedule" on the employee app).
        const customTimeEntries = Object.entries(addDaysTimes).filter(([k, v]) => {
          const [, d] = k.split('|');
          return d === date && (v?.start || v?.end);
        });
        if (customTimeEntries.length) {
          const schedRes = await axios.get(`${API}/admin/event-schedule`, {
            params: { open_shift_ids: newOpenShiftId },
          });
          const staffList = schedRes.data?.staff || [];
          for (const [key, val] of customTimeEntries) {
            const [uid] = key.split('|');
            const row = staffList.find((s) => s.user_id === uid);
            const dayShifts = row?.shifts_by_date?.[date] || [];
            const sid = dayShifts[0]?.shift_id;
            if (sid && (val.start || val.end)) {
              try {
                await axios.put(`${API}/shifts/${sid}`, {
                  start_time: val.start || sourceShift?.start_time || '',
                  end_time: val.end || sourceShift?.end_time || '',
                });
              } catch (_) {}
            }
          }
        }
      }
      toast.success(`Added ${addDaysDates.length} day(s) for ${pickedUserIds.length} staff`);
      closeAddDaysDialog();
      fetchOpenShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add days');
    } finally {
      setAddDaysSaving(false);
    }
  };

  // Add an existing employee to another day of the same event

  // ── Extra Staff helpers ──────────────────────────────────────────────────
  // Per-card local state so each event card has its own "Add Extra Staff"
  // search box + selected-dates list.
  const getExtra = (groupKey) => extraStaffState[groupKey] || { search: '', selectedDates: [], userId: null };
  const setExtra = (groupKey, patch) => {
    setExtraStaffState(prev => ({ ...prev, [groupKey]: { ...getExtra(groupKey), ...patch } }));
  };
  const toggleExtraDate = (groupKey, date) => {
    const cur = getExtra(groupKey);
    const dates = cur.selectedDates.includes(date)
      ? cur.selectedDates.filter(d => d !== date)
      : [...cur.selectedDates, date].sort();
    setExtra(groupKey, { selectedDates: dates });
  };
  const handleAddExtraStaff = async (group) => {
    const { userId, selectedDates } = getExtra(group.key);
    if (!userId) {
      toast.error('Pick a staff member first');
      return;
    }
    if (selectedDates.length === 0) {
      toast.error('Pick at least one day');
      return;
    }
    const shiftIds = group.shifts.filter(s => selectedDates.includes(s.date)).map(s => s.id);
    try {
      const notify = askNotify();
      const res = await axios.post(`${API}/open-shifts/bulk-add-extra-staff?notify=${notify}`, { user_id: userId, shift_ids: shiftIds });
      const { added = 0, skipped = 0 } = res.data || {};
      if (added > 0) {
        toast.success(`Added as extra to ${added} day${added !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped)` : ''}`);
      } else {
        toast.info(res.data?.message || 'Could not add as extra');
      }
      // Reset selection but keep the section open
      setExtra(group.key, { search: '', selectedDates: [], userId: null });
      fetchOpenShifts();
    } catch (err) {
      // Surface the real backend message + HTTP status so admin can tell us
      // exactly what failed when reporting an issue (improves debuggability).
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = detail
        ? `${detail}${status ? ` (HTTP ${status})` : ''}`
        : status
          ? `Add extra failed (HTTP ${status})`
          : `Add extra failed: ${err.message || 'network error'}`;
      console.error('bulk-add-extra-staff failed:', { status, detail, raw: err });
      toast.error(msg);
    }
  };

  const getDateRange = (group) => {
    const dates = [...new Set((group.shifts || []).map(s => s.date).filter(Boolean))].sort();
    if (dates.length === 0) return '';
    if (dates.length === 1) return formatDate(dates[0]);
    try {
      return `${format(parseISO(dates[0]), 'MMM d')} - ${format(parseISO(dates[dates.length - 1]), 'MMM d, yyyy')}`;
    } catch {
      return `${dates[0]} - ${dates[dates.length - 1]}`;
    }
  };

  const getTotalClaimed = (group) => {
    const employees = getGroupEmployees(group);
    return employees.length;
  };

  const getTotalSlots = (group) => {
    return group.shifts.reduce((sum, s) => sum + (s.quantity || 1), 0);
  };

  // Render a grouped event card
  const renderGroupCard = (group, isFull = false) => {
    const isExpanded = expandedGroups[group.key];
    const employees = getGroupEmployees(group);
    const dateRange = getDateRange(group);
    const dayCount = new Set(group.shifts.map(s => s.date)).size;
    // Detect duplicate open_shifts on the same date inside this event group.
    const hasDuplicates = group.shifts.length > dayCount;

    return (
      <Card
        key={group.key}
        className={cn(
          "bg-card border-border rounded-sm transition-colors",
          isFull ? "border-green-500/30" : "hover:border-blue-500/50"
        )}
        data-testid={`event-group-${group.key}`}
      >
        <CardContent className="p-4 space-y-3">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={cn(
                "border",
                isFull
                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                  : "bg-blue-500/20 text-blue-400 border-blue-500/30"
              )}>
                {employees.length} staff | {dayCount} day{dayCount !== 1 ? 's' : ''}
              </Badge>
              {group.source === 'client_request' && (
                <Badge className="border bg-primary/15 text-primary border-primary/40 uppercase tracking-wider text-[10px]" data-testid={`client-badge-${group.key}`}>
                  Client: {group.company_name || 'portal'}
                </Badge>
              )}
              {group.category && (
                <Badge className="border bg-purple-500/15 text-purple-300 border-purple-500/40 uppercase tracking-wider text-[10px]">
                  {group.category}
                </Badge>
              )}
              {(() => {
                const cat = (group.shift_category || 'event').toLowerCase();
                if (cat === 'retail') {
                  return (
                    <Badge
                      className="border bg-blue-500/15 text-blue-300 border-blue-500/40 uppercase tracking-wider text-[10px]"
                      data-testid={`event-category-badge-${group.key}`}
                      title="Retail deployment"
                    >
                      Retail
                    </Badge>
                  );
                }
                return (
                  <Badge
                    className="border bg-orange-500/15 text-orange-300 border-orange-500/40 uppercase tracking-wider text-[10px]"
                    data-testid={`event-category-badge-${group.key}`}
                    title="Event / Festival deployment"
                  >
                    Event/Festival
                  </Badge>
                );
              })()}
            </div>
            <div className="flex gap-1">
              {!opsMode && hasDuplicates && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleConsolidateDuplicates(group)}
                  disabled={consolidatingKey === group.key}
                  className="h-7 w-7 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 animate-pulse"
                  title="Consolidate duplicate shifts for this event"
                  data-testid={`consolidate-group-${group.key}`}
                >
                  {consolidatingKey === group.key
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Layers className="w-4 h-4" />}
                </Button>
              )}
            </div>
          </div>

          {/* Event info */}
          <div className="space-y-2">
            <p className="text-lg font-medium text-foreground flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-primary" />
              {group.position}
              {!opsMode && (() => {
                const touched = (group.shifts || []).filter((s) => s.dept_touch).sort((a, b) => (b.dept_touch.at || '').localeCompare(a.dept_touch.at || ''))[0];
                return touched ? <DeptBadge deptTouch={touched.dept_touch} targetId={touched.id} /> : null;
              })()}
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {group.location}
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {dateRange}
            </p>
            <p className="text-[11px] text-muted-foreground/70 italic ml-6 -mt-1">
              Times set per staff in Shift Calendar
            </p>
            {(() => {
              const tot = group.shifts.reduce((acc, s) => {
                const h = hoursSummary[s.id];
                if (h) {
                  acc.sch += h.scheduled_hours || 0;
                  acc.wrk += h.worked_hours || 0;
                  acc.pend += h.pending_times || 0;
                }
                return acc;
              }, { sch: 0, wrk: 0, pend: 0 });
              if (!tot.sch && !tot.wrk && !tot.pend) return null;
              const fmt = (n) => Number(n.toFixed(2));
              return (
                <div
                  className="flex flex-wrap items-center gap-2 ml-6"
                  data-testid={`hours-summary-${group.key}`}
                >
                  <Badge className="border bg-sky-500/15 text-sky-300 border-sky-500/40 text-[11px]">
                    Scheduled: {fmt(tot.sch)} h
                  </Badge>
                  <Badge className="border bg-green-500/15 text-green-400 border-green-500/40 text-[11px]">
                    Worked: {fmt(tot.wrk)} h
                  </Badge>
                  {tot.pend > 0 && (
                    <span className="text-[10px] text-amber-400/80 italic" title="Shifts without confirmed times are not counted in Scheduled">
                      +{tot.pend} no times set
                    </span>
                  )}
                </div>
              );
            })()}
            {group.dress_code && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Shirt className="w-4 h-4" />
                {group.dress_code}
              </p>
            )}
            {group.hourly_payment && (
              <p className="text-sm font-medium text-green-400 flex items-center gap-2">
                <PoundSterling className="w-4 h-4" />
                {currencySymbol(group.currency)}{group.hourly_payment.toFixed(2)} / hour
              </p>
            )}
            {group.sia_required && group.sia_required !== 'none' && (
              <p className="text-sm text-yellow-400 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                SIA: {group.sia_required === 'door_supervisor' ? 'Door Supervisor' : 'CCTV'}
              </p>
            )}
            {(() => {
              const over = group.shifts
                .filter((s) => ((s.claimed_by_list || []).length || s.claimed_count || 0) > (s.quantity || 1))
                .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
              if (over.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1.5" data-testid={`overstaffed-row-${group.key}`}>
                  {over.map((s) => {
                    const assigned = (s.claimed_by_list || []).length || s.claimed_count || 0;
                    return (
                      <span
                        key={s.id}
                        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30"
                        title="More staff assigned than required — nobody is removed automatically"
                      >
                        {formatDate(s.date)}: {assigned}/{s.quantity || 1} · Over-staffed +{assigned - (s.quantity || 1)}
                      </span>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Expand/collapse — extra details (staff list + extra-staff form) */}
          <button
            onClick={() => toggleGroup(group.key)}
            className="w-full flex items-center justify-between p-2 bg-accent/50 rounded-sm hover:bg-accent transition-colors"
            data-testid={`toggle-group-${group.key}`}
          >
            <span className="text-sm text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" />
              {employees.length} employee{employees.length !== 1 ? 's' : ''} assigned
            </span>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>

          {/* Expanded: show employee names */}
          {isExpanded && (
            <div className="space-y-2 pl-2 border-l-2 border-primary/30">
              {employees.length > 0 ? (
                employees.map((emp) => {
                  const allDates = [...new Set(group.shifts.map(s => s.date))].sort();
                  return (
                    <div key={emp.user_id} className="py-1.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">{emp.user_name}</span>
                        <ComplianceIndicator userId={emp.user_id} />
                        <Badge variant="outline" className="text-xs">{emp.user_role}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1.5 ml-5">
                        {allDates.map((d) => {
                          const isAssigned = emp.dates.includes(d);
                          const isExtra = emp.extraDates && emp.extraDates.includes(d);
                          const targetShift = group.shifts.find(s => s.date === d);
                          const isFull = targetShift && (targetShift.claimed_count || 0) >= (targetShift.quantity || 1);
                          if (isAssigned) {
                            return (
                              <span
                                key={d}
                                className={cn(
                                  'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm border',
                                  isExtra
                                    ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                                    : 'bg-green-500/15 text-green-400 border-green-500/30'
                                )}
                                title={isExtra ? `Extra on ${d}` : `Working on ${d}`}
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                {format(parseISO(d), 'EEE d')}
                                {isExtra && <span className="ml-0.5 text-[9px] uppercase tracking-wider opacity-80">+x</span>}
                              </span>
                            );
                          }
                          return (
                            <button
                              key={d}
                              type="button"
                              disabled={isFull}
                              onClick={() => handleAddStaffToDate(group, emp.user_id, d)}
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm border transition-colors',
                                isFull
                                  ? 'border-border text-muted-foreground/50 cursor-not-allowed opacity-50'
                                  : 'border-dashed border-border text-muted-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/40'
                              )}
                              title={isFull ? `${d} is fully booked` : `Add to ${d}`}
                              data-testid={`add-staff-${emp.user_id}-${d}`}
                            >
                              <Plus className="w-3 h-3" />
                              {format(parseISO(d), 'EEE d')}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-xs text-muted-foreground py-1">No staff assigned yet</p>
              )}
            </div>
          )}

          {/* Extra Staff section — only shown when expanded.
              Lets admin add a named staff member to specific days
              OVER the planned headcount. */}
          {isExpanded && (() => {
            const extra = getExtra(group.key);
            const allDates = [...new Set(group.shifts.map(s => s.date))].sort();
            const matchingUsers = extra.search
              ? allUsers
                  .filter(u => u.role !== 'admin' && (u.name?.toLowerCase().includes(extra.search.toLowerCase()) || u.email?.toLowerCase().includes(extra.search.toLowerCase())))
                  .slice(0, 8)
              : [];
            const pickedUser = extra.userId ? allUsers.find(u => u.id === extra.userId) : null;
            const daysOnFor = (uid) => allDates.filter(d => {
              const ts = group.shifts.find(s => s.date === d);
              return ts && (ts.claimed_by_list || []).some(c => c.user_id === uid);
            }).length;
            const pickedOnAllDays = pickedUser && daysOnFor(pickedUser.id) === allDates.length;
            return (
              <div className="pt-3 mt-1 border-t border-dashed border-amber-500/30 space-y-2" data-testid={`extra-staff-${group.key}`}>
                <Label className="text-xs uppercase tracking-wider text-amber-400 flex items-center gap-2">
                  <UserPlus className="w-3.5 h-3.5" /> Extra Staff (over capacity)
                </Label>
                <p className="text-[11px] text-muted-foreground">
                  Add a staff member on top of the planned headcount, for one or more specific days.
                </p>
                {/* Step 1: pick user */}
                {!pickedUser ? (
                  <div className="space-y-1">
                    <Input
                      placeholder="Search staff..."
                      value={extra.search}
                      onChange={(e) => setExtra(group.key, { search: e.target.value })}
                      className="bg-background border-border rounded-sm h-8 text-sm"
                      data-testid={`extra-staff-search-${group.key}`}
                    />
                    {extra.search && matchingUsers.length > 0 && (
                      <div className="max-h-32 overflow-y-auto space-y-1 border border-border rounded-sm bg-background">
                        {matchingUsers.map(u => {
                          const onDays = daysOnFor(u.id);
                          return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => setExtra(group.key, { userId: u.id, search: '' })}
                            className="w-full flex items-center justify-between px-2 py-1.5 text-sm hover:bg-amber-500/10 text-left"
                            data-testid={`extra-staff-pick-${u.id}`}
                          >
                            <span className={onDays === allDates.length ? 'text-muted-foreground' : ''}>{u.name}</span>
                            <span className="flex items-center gap-1.5">
                              {onDays > 0 && (
                                <Badge variant="outline" className="text-[10px] border-green-500/40 text-green-400" data-testid={`extra-staff-ondays-${u.id}`}>
                                  {onDays === allDates.length ? 'on all days' : `on ${onDays}/${allDates.length} days`}
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                            </span>
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                    <span className="text-sm flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-amber-400" />
                      {pickedUser.name}
                      <Badge variant="outline" className="text-[10px]">{pickedUser.role}</Badge>
                    </span>
                    <button
                      type="button"
                      onClick={() => setExtra(group.key, { userId: null, selectedDates: [] })}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`extra-staff-clear-${group.key}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {/* Step 2: pick dates (only after user selected) */}
                {pickedUser && (
                  <>
                    {pickedOnAllDays ? (
                      <div className="p-2 bg-green-500/10 border border-green-500/30 rounded-sm text-[12px] text-green-400 flex items-start gap-2" data-testid={`extra-staff-all-days-notice-${group.key}`}>
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>
                          <strong>{pickedUser.name}</strong> is already assigned to every day of this event, so there is nothing to add.
                          Pick a different staff member, or remove them from a day first (Manage Event → Manage Staff).
                        </span>
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground pt-1">Pick the day(s) for the extra: <span className="text-green-400/70">(green = already assigned that day)</span></p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {allDates.map(d => {
                        const picked = extra.selectedDates.includes(d);
                        const targetShift = group.shifts.find(s => s.date === d);
                        const alreadyOn = targetShift && (targetShift.claimed_by_list || []).some(c => c.user_id === pickedUser.id);
                        return (
                          <button
                            key={d}
                            type="button"
                            disabled={alreadyOn}
                            onClick={() => toggleExtraDate(group.key, d)}
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-sm border transition-colors',
                              alreadyOn
                                ? 'border-green-500/30 bg-green-500/10 text-green-400/60 cursor-not-allowed'
                                : picked
                                  ? 'border-amber-500/60 bg-amber-500/20 text-amber-300'
                                  : 'border-dashed border-border text-muted-foreground hover:bg-amber-500/10 hover:text-amber-400 hover:border-amber-500/40'
                            )}
                            title={alreadyOn ? 'Already on this day' : (picked ? 'Picked' : 'Tap to pick')}
                            data-testid={`extra-date-${group.key}-${d}`}
                          >
                            {alreadyOn ? <CheckCircle2 className="w-3 h-3" /> : picked ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                            {format(parseISO(d), 'EEE d')}
                          </button>
                        );
                      })}
                    </div>
                    <Button
                      onClick={() => handleAddExtraStaff(group)}
                      disabled={extra.selectedDates.length === 0}
                      className={cn('w-full mt-2 bg-amber-500 hover:bg-amber-400 text-black rounded-sm h-8 text-sm font-semibold', pickedOnAllDays && 'hidden')}
                      data-testid={`extra-staff-submit-${group.key}`}
                    >
                      <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                      Add as Extra to {extra.selectedDates.length} day{extra.selectedDates.length !== 1 ? 's' : ''}
                    </Button>
                  </>
                )}
              </div>
            );
          })()}

          {/* ---- Simplified bottom action bar (Variant B) ----
              Two visible buttons + a "More" dropdown for the rest.
              Keeps the card short while preserving every existing action. */}
          <div className="flex gap-2 pt-2 border-t border-border">
            <Button
              onClick={() => navigate(`/admin/event-board/${group.shifts[0]?.id}`)}
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              data-testid={`open-board-${group.key}`}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Open Board
            </Button>
            <Button
              variant="outline"
              onClick={() => setScheduleViewGroup(group)}
              className="flex-1 border-primary/40 text-primary hover:bg-primary/10 rounded-sm"
              data-testid={`view-schedule-${group.key}`}
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              View Schedule
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex-1 border-border rounded-sm"
                  data-testid={`manage-event-menu-${group.key}`}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Event
                  <MoreVertical className="w-3.5 h-3.5 ml-2 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">Staff</DropdownMenuLabel>
                {group.shifts[0] && (
                  <DropdownMenuItem
                    onClick={() => { setManageShift(group.shifts[0]); setManageGroup(group); }}
                    data-testid={`menu-manage-${group.key}`}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Manage Staff
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    setExpandedGroups(prev => ({ ...prev, [group.key]: true }));
                    setTimeout(() => {
                      const el = document.querySelector(`[data-testid="extra-staff-${group.key}"]`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 60);
                  }}
                  data-testid={`menu-add-extra-${group.key}`}
                >
                  <UserPlus className="w-4 h-4 mr-2 text-amber-400" />
                  Add Extra Staff
                </DropdownMenuItem>
                {!opsMode && (
                <DropdownMenuItem
                  onClick={() => {
                    const ids = group.shifts.map(s => s.id);
                    const firstShift = group.shifts[0] || {};
                    const dates = [...new Set(group.shifts.map(s => s.date))].sort();
                    const dateLabel = dates.length === 1
                      ? dates[0]
                      : `${dates[0]} → ${dates[dates.length - 1]} (${dates.length} days)`;
                    setImportTargetGroup({
                      openShiftIds: ids,
                      label: `${firstShift.location || ''}||${firstShift.position || ''}`,
                      displayName: `${firstShift.position || 'Event'} — ${firstShift.location || ''} (${dateLabel})`,
                    });
                    setImportExcelOpen(true);
                  }}
                  data-testid={`menu-import-excel-${group.key}`}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-400" />
                  Import Excel
                </DropdownMenuItem>
                )}
                {!opsMode && (
                <DropdownMenuItem
                  onClick={() => handleDownloadSchedule(group)}
                  data-testid={`menu-download-schedule-${group.key}`}
                >
                  <Download className="w-4 h-4 mr-2 text-primary" />
                  Download Schedule
                </DropdownMenuItem>
                )}
                {!opsMode && group.shifts.some(s => (s.claimed_count || 0) < (s.quantity || 1)) && (
                  <DropdownMenuItem
                    onClick={() => handleEmailAvailableStaff(group)}
                    data-testid={`menu-email-staff-${group.key}`}
                  >
                    <Mail className="w-4 h-4 mr-2 text-blue-400" />
                    Email Available Staff
                  </DropdownMenuItem>
                )}
                {!opsMode && (
                <DropdownMenuItem
                  onClick={() => handleEmailStaffSchedule(group)}
                  data-testid={`menu-email-schedule-${group.key}`}
                >
                  <Mail className="w-4 h-4 mr-2 text-green-400" />
                  Email Staff Schedule
                </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Event</DropdownMenuLabel>
                {group.shifts[0] && (
                  <DropdownMenuItem
                    onClick={() => openAddDaysDialog(group)}
                    data-testid={`menu-add-days-${group.key}`}
                  >
                    <CalendarPlus className="w-4 h-4 mr-2 text-emerald-400" />
                    Add Days & Staff
                  </DropdownMenuItem>
                )}
                {group.shifts[0] && (
                  <DropdownMenuItem
                    onClick={() => openBatchEditDialog(group)}
                    data-testid={`menu-edit-event-${group.key}`}
                  >
                    <Edit className="w-4 h-4 mr-2 text-primary" />
                    Edit Event
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => openCapacityDialog(group)}
                  data-testid={`menu-edit-capacity-${group.key}`}
                >
                  <Users className="w-4 h-4 mr-2 text-amber-400" />
                  Edit Day Capacity
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs">Tools</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => setSetTimesDialog({
                    open: true,
                    group,
                    start_time: group.start_time || '',
                    end_time: group.end_time || '',
                  })}
                  data-testid={`menu-set-times-${group.key}`}
                >
                  <Clock className="w-4 h-4 mr-2 text-primary" />
                  Set Schedule for All Staff
                </DropdownMenuItem>
                {!opsMode && (
                <DropdownMenuItem
                  onClick={() => handleGenerateBadge(group)}
                  data-testid={`menu-qr-${group.key}`}
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  QR Badge
                </DropdownMenuItem>
                )}
                {(group.start_time || group.end_time) && (
                  <DropdownMenuItem
                    onClick={() => setResetTimesDialog({ open: true, group })}
                    data-testid={`menu-reset-times-${group.key}`}
                  >
                    <Clock className="w-4 h-4 mr-2 text-amber-400" />
                    Clear Stored Times → Pending
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-destructive">Danger zone</DropdownMenuLabel>
                {!opsMode && (
                <DropdownMenuItem
                  onClick={async () => {
                    if (!window.confirm(`Move "${group.location}" (${group.shifts.length} day${group.shifts.length > 1 ? 's' : ''}) to Archive? The event becomes read-only and will appear under Archived Events.`)) return;
                    try {
                      const ids = group.shifts.map(s => s.id);
                      const res = await axios.post(`${API}/open-shifts/archive-group`, { shift_ids: ids });
                      toast.success(res.data?.message || 'Event archived');
                      fetchOpenShifts();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Archive failed');
                    }
                  }}
                  data-testid={`menu-archive-${group.key}`}
                >
                  <Archive className="w-4 h-4 mr-2 text-slate-300" />
                  Archive Event
                </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => handleCopyEvent(group)}
                  data-testid={`menu-copy-${group.key}`}
                >
                  <CalendarPlus className="w-4 h-4 mr-2 text-primary" />
                  Copy to next week
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setCancelEventDialog({ open: true, group })}
                  className="text-destructive focus:text-destructive"
                  data-testid={`menu-cancel-${group.key}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {opsMode ? 'Request Event Deletion' : 'Delete Event'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </Layout>
    );
  }

  const monthData = getMonthDays(currentMonth);

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            {(isEmbedded || new URLSearchParams(window.location.search).get('focus')) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(opsMode ? '/employee/ops/events' : '/admin/shifts/events')}
                className="rounded-sm text-xs uppercase mb-2"
                data-testid="back-to-calendar-btn"
              >
                ← Back to Event Calendar
              </Button>
            )}
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <CalendarPlus className="w-6 h-6 text-primary" strokeWidth={1.5} />
              {isCreatePage ? 'CREATE EVENT' : isEventPage ? eventPageName.toUpperCase() : 'UPCOMING SHIFTS'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isCreatePage
                ? 'Fill in the form to create a new event'
                : isEventPage
                  ? 'Manage this event — staff, schedule, days and capacity'
                  : 'Create and manage shifts for staff to claim'}
            </p>
          </div>
          
          {!isEmbedded && (
          <div className="flex flex-wrap gap-2">
            {!opsMode && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  toast.info('Cleaning duplicate shifts...');
                  const res = await axios.post(`${API}/admin/sync-shifts`);
                  const removed = res.data?.dedup_removed || 0;
                  if (removed > 0) {
                    toast.success(`Removed ${removed} duplicate shift(s). Reloading…`);
                  } else {
                    toast.success('No duplicates found. Everything is clean!');
                  }
                  fetchOpenShifts();
                } catch (_e) {
                  toast.error('Cleanup failed');
                }
              }}
              data-testid="cleanup-duplicates-btn"
              className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10 rounded-sm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Cleanup Duplicates
            </Button>
            )}
            {!opsMode && (
            <Button variant="outline" onClick={handleExport} className="border-border rounded-sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            )}
            {!opsMode && (
            <Button
              variant="outline"
              onClick={() => { setImportTargetGroup(null); setImportExcelOpen(true); }}
              data-testid="import-excel-btn"
              className="border-green-500/40 text-green-400 hover:bg-green-500/10 rounded-sm"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import Excel
            </Button>
            )}
            <Button onClick={() => setCreateOpen(true)} data-testid="create-open-shift-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider btn-glow">
              <Plus className="w-4 h-4 mr-2" />
              Add Shifts
            </Button>
          </div>
          )}
        </div>

        {/* Rota Excel allocation — dedicated event page only */}
        {isEventPage && <RotaAllocationSection eventName={eventPageName} />}

        {/* Filters */}
        {!isEmbedded && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by location or position..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-background border-border rounded-sm" />
          </div>
          <div className="flex gap-2">
            <Button variant={filterStatus === 'all' ? 'default' : 'outline'} onClick={() => setFilterStatus('all')} className={cn("rounded-sm", filterStatus === 'all' && "bg-primary text-black")}>
              All ({openShifts.length})
            </Button>
            <Button variant={filterStatus === 'available' ? 'default' : 'outline'} onClick={() => setFilterStatus('available')} className={cn("rounded-sm", filterStatus === 'available' && "bg-blue-500 text-foreground")}>
              Available ({availableShifts.length})
            </Button>
            <Button variant={filterStatus === 'full' ? 'default' : 'outline'} onClick={() => setFilterStatus('full')} className={cn("rounded-sm", filterStatus === 'full' && "bg-green-500 text-foreground")}>
              Full ({fullShifts.length})
            </Button>
          </div>
        </div>

        )}

        {/* Category calendars: Labour / Security / Retail */}
        {!isEmbedded && (
        <div className="flex flex-wrap gap-2" data-testid="category-filter-row">
          {[['all', 'All Calendars'], ['labour', 'Labour'], ['security', 'Security'], ['retail', 'Retail']].map(([key, label]) => (
            <Button
              key={key}
              size="sm"
              variant={filterCategory === key ? 'default' : 'outline'}
              onClick={() => setFilterCategory(key)}
              data-testid={`calendar-filter-${key}`}
              className={cn('rounded-sm text-xs uppercase tracking-wider', filterCategory === key && 'bg-primary text-primary-foreground')}
            >
              {label}
            </Button>
          ))}
        </div>
        )}

        {/* Group ALL filtered shifts (regardless of per-day fullness) and
            then split by GROUP-level fullness. This keeps a multi-day event
            on a single card even when some days are still open and others
            are full. */}
        {!isCreatePage && (() => {
          const allGroups = groupShifts(filteredShifts);
          const isGroupFull = (g) => g.shifts.every(
            s => (s.claimed_count || 0) >= (s.quantity || 1)
          );
          const availableGroups = allGroups.filter(g => !isGroupFull(g));
          const fullGroups = allGroups.filter(isGroupFull);
          return (
            <>
              {(filterStatus === 'all' || filterStatus === 'available') && availableGroups.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-sm font-barlow tracking-wider uppercase text-blue-400 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Available Events ({availableGroups.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin">
                    {availableGroups.map((group) => renderGroupCard(group, false))}
                  </div>
                </div>
              )}

              {(filterStatus === 'all' || filterStatus === 'full') && fullGroups.length > 0 && (
                <div className="space-y-4">
                  <h2 className="text-sm font-barlow tracking-wider uppercase text-green-400 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Full Events ({fullGroups.length})
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin">
                    {fullGroups.map((group) => renderGroupCard(group, true))}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Empty State */}
        {!isCreatePage && filteredShifts.length === 0 && (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center">
              <CalendarPlus className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Shifts Found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchTerm || filterStatus !== 'all' ? 'Try adjusting your filters' : 'Create shifts for staff to claim'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <Button onClick={() => setCreateOpen(true)} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Shifts
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Shift Dialog */}
        <Dialog open={createOpen || !!editShift} onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false); setEditShift(null); resetForm();
            if (isCreatePage) navigate(opsMode ? '/employee/ops/events' : '/admin/shifts/events');
          }
        }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                {editShift ? 'Edit Shift' : 'Create Open Shifts'}
              </DialogTitle>
            </DialogHeader>

            {/* Multi-event tabs (only in create mode) */}
            {!editShift && eventForms.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-2 pb-2 border-b border-border">
                {eventForms.map((ev, idx) => (
                  <div key={idx} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setActiveEventIndex(idx)}
                      className={cn(
                        "px-3 py-1.5 rounded-sm text-xs font-medium transition-colors",
                        activeEventIndex === idx
                          ? "bg-primary text-primary-foreground"
                          : "bg-accent text-muted-foreground hover:text-foreground"
                      )}
                      data-testid={`event-tab-${idx}`}
                    >
                      Event {idx + 1}
                      {ev.position ? `: ${ev.position.substring(0, 15)}` : ''}
                      {ev.selectedDates.length > 0 ? ` (${ev.selectedDates.length}d)` : ''}
                    </button>
                    {eventForms.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEvent(idx)}
                        className="ml-1 p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                        data-testid={`remove-event-${idx}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addEvent}
                  className="px-3 py-1.5 rounded-sm text-xs font-medium border border-dashed border-primary text-primary hover:bg-primary/10 transition-colors flex items-center gap-1"
                  data-testid="add-event-btn"
                >
                  <Plus className="w-3 h-3" /> Add Event
                </button>
              </div>
            )}

            <form onSubmit={editShift ? handleUpdateShift : handleCreateShift} className="space-y-4 mt-4">
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Position
                  </Label>
                  <Input value={shiftForm.position} onChange={(e) => setShiftForm({ ...shiftForm, position: e.target.value })} placeholder="e.g., Security Guard" className="bg-background border-border rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Location
                  </Label>
                  <Input value={shiftForm.location} onChange={(e) => setShiftForm({ ...shiftForm, location: e.target.value })} placeholder="e.g., Main Office" className="bg-background border-border rounded-sm" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Shirt className="w-4 h-4" /> Dress Code
                </Label>
                <Input value={shiftForm.dress_code} onChange={(e) => setShiftForm({ ...shiftForm, dress_code: e.target.value })} placeholder="e.g., Full Black Suit" className="bg-background border-border rounded-sm" />
              </div>

              {/* Geofenced clock-in — optional */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border border-border rounded-sm p-3 bg-muted/40">
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Clock-in location link (optional)
                  </Label>
                  <Input
                    data-testid="event-location-link-input"
                    value={shiftForm.location_link || ''}
                    onChange={(e) => setShiftForm({ ...shiftForm, location_link: e.target.value })}
                    placeholder="Paste Google Maps or what3words link"
                    className="bg-background border-border rounded-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEventMapOpen(true)}
                    className="h-8 rounded-sm text-xs w-full sm:w-auto"
                    data-testid="event-pick-on-map-btn"
                  >
                    <MapPin className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> Pick on map
                  </Button>
                  <MapPickerDialog
                    open={eventMapOpen}
                    onOpenChange={setEventMapOpen}
                    initialLink={shiftForm.location_link}
                    postcode={shiftForm.postcode}
                    siteName={shiftForm.location || shiftForm.site_name}
                    onPick={(link) => setShiftForm((f) => ({ ...f, location_link: link }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Allowed distance</Label>
                  <select
                    data-testid="event-geo-radius-select"
                    value={shiftForm.geo_radius || ''}
                    onChange={(e) => setShiftForm({ ...shiftForm, geo_radius: e.target.value })}
                    className="w-full h-9 bg-background border border-border rounded-sm px-2 text-sm"
                  >
                    <option value="">No limit</option>
                    <option value="25">25 m</option>
                    <option value="50">50 m</option>
                    <option value="100">100 m</option>
                    <option value="200">200 m</option>
                    <option value="500">500 m</option>
                  </select>
                  <p className="text-[10px] text-muted-foreground">Staff outside this radius CANNOT clock in.</p>
                </div>
              </div>

              {/* Single Month Calendar - Like Photo */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {editShift ? 'Date' : `Select Dates (${shiftForm.selectedDates.length} selected)`}
                </Label>
                
                <div className="bg-muted border border-border rounded-sm p-4">
                  {/* Month Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium uppercase tracking-wider text-primary">{monthData.monthLabel}</span>
                    <div className="flex gap-1">
                      <Button type="button" variant="outline" size="icon" onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="h-8 w-8 border-border">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button type="button" variant="outline" size="icon" onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="h-8 w-8 border-border">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Calendar Grid */}
                  <div className="border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between mb-3">
                      <Button type="button" variant="ghost" size="icon" onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="h-10 w-10 text-muted-foreground hover:text-foreground">
                        <ChevronLeft className="w-5 h-5" />
                      </Button>
                      <span className="text-lg font-medium">{monthData.monthLabel}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="h-10 w-10 text-muted-foreground hover:text-foreground">
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Day Headers */}
                    <div className="grid grid-cols-7 mb-2">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d, i) => (
                        <div key={i} className="text-center text-sm text-muted-foreground py-2">{d}</div>
                      ))}
                    </div>

                    {/* Day Numbers */}
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: monthData.startPadding }).map((_, i) => (
                        <div key={`pad-${i}`} className="h-10" />
                      ))}
                      {monthData.days.map((day) => (
                        <button
                          key={day.dateStr}
                          type="button"
                          onClick={() => {
                            if (editShift) {
                              setShiftForm({ ...shiftForm, selectedDates: [day.dateStr] });
                            } else {
                              handleDateToggle(day.dateStr);
                            }
                          }}
                          className={cn(
                            "h-10 w-full text-lg rounded-sm flex items-center justify-center transition-all font-medium",
                            shiftForm.selectedDates.includes(day.dateStr)
                              ? "bg-primary text-black"
                              : day.isToday
                                ? "bg-blue-500/30 text-blue-300 border border-blue-500/50"
                                : "hover:bg-accent text-foreground"
                          )}
                        >
                          {day.dayNum}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Selected Date Summary */}
                  {shiftForm.selectedDates.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider">
                        {shiftForm.selectedDates.length === 1 
                          ? format(parseISO(shiftForm.selectedDates[0]), 'EEEE, MMM d')
                          : `${shiftForm.selectedDates.length} dates selected`
                        }
                      </p>
                      {shiftForm.selectedDates.length > 1 && (
                        <>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {shiftForm.selectedDates.sort().slice(0, 10).map(d => (
                              <Badge key={d} variant="outline" className="text-xs bg-primary/20 text-primary border-primary/50">
                                {format(parseISO(d), 'MMM d')}
                              </Badge>
                            ))}
                            {shiftForm.selectedDates.length > 10 && (
                              <Badge variant="outline" className="text-xs">+{shiftForm.selectedDates.length - 10} more</Badge>
                            )}
                          </div>
                          {/* Per-day quantity override — lets admin set a different
                              headcount on individual days of the same event. */}
                          <div className="mt-3 space-y-1.5">
                            {(() => {
                              const bdTotal =
                                (parseInt(shiftForm.staff_breakdown?.sia_door_supervisor) || 0) +
                                (parseInt(shiftForm.staff_breakdown?.cso_steward) || 0) +
                                (parseInt(shiftForm.staff_breakdown?.cleaner) || 0) +
                                (parseInt(shiftForm.staff_breakdown?.supervisor) || 0);
                              const defaultQty = bdTotal > 0 ? bdTotal : (parseInt(shiftForm.quantity) || 1);
                              return (
                                <>
                                  <p className="text-[11px] text-muted-foreground">
                                    <strong>Staff per day</strong> — leave blank to use the Total from Staff Requirements ({defaultQty}).
                                  </p>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto pr-1">
                                    {shiftForm.selectedDates.sort().map((d) => (
                                      <div key={d} className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground w-14 shrink-0">
                                          {format(parseISO(d), 'EEE d')}
                                        </span>
                                        <Input
                                          type="number"
                                          min="1"
                                          max="500"
                                          placeholder={String(defaultQty)}
                                          value={(shiftForm.quantity_by_date || {})[d] || ''}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            setShiftForm((prev) => {
                                              const next = { ...(prev.quantity_by_date || {}) };
                                              if (v === '' || v === null) delete next[d];
                                              else next[d] = v;
                                              return { ...prev, quantity_by_date: next };
                                      });
                                    }}
                                    className="bg-background border-border rounded-sm h-7 text-sm w-16"
                                    data-testid={`qty-day-${d}`}
                                  />
                                </div>
                              ))}
                            </div>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {!editShift && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setShiftForm({ ...shiftForm, selectedDates: [] })} className="text-xs border-border">
                    Clear All
                  </Button>
                )}
              </div>
              
              {/* Time Selection — OPTIONAL. Leave empty to mark as Pending
                  (per-staff times are set later in Shift Calendar). */}
              <div className="space-y-1">
                <p className="text-[11px] text-muted-foreground italic">
                  Leave times empty if the client hasn't confirmed yet — they'll show as <span className="text-amber-400 font-semibold">PENDING</span> everywhere until you set them per staff in Shift Calendar.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Time <span className="text-muted-foreground/60 normal-case">(optional)</span></Label>
                    <Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} className="bg-background border-border rounded-sm" data-testid="event-start-time" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Time <span className="text-muted-foreground/60 normal-case">(optional)</span></Label>
                    <Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} className="bg-background border-border rounded-sm" data-testid="event-end-time" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <PoundSterling className="w-4 h-4" /> Hourly Rate ({currencySymbol(shiftForm.currency)})
                  </Label>
                  <Input type="number" step="0.01" min="0" value={shiftForm.hourly_payment} onChange={(e) => setShiftForm({ ...shiftForm, hourly_payment: e.target.value })} placeholder="e.g., 12.50" className="bg-background border-border rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Shield className="w-4 h-4" /> SIA Required
                  </Label>
                  <Select value={shiftForm.sia_required} onValueChange={(val) => setShiftForm({ ...shiftForm, sia_required: val })}>
                    <SelectTrigger className="bg-background border-border rounded-sm">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="door_supervisor">Door Supervisor</SelectItem>
                      <SelectItem value="cctv">CCTV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Staff Requirements — per-role headcount breakdown + rate per role.
                  Fully OPTIONAL: admin can leave every role at 0 and the event
                  still saves. Only when at least one role has a positive count
                  does it override the event's default capacity. */}
              <StaffRequirementsBlock
                value={shiftForm.staff_breakdown}
                onChange={(next) => setShiftForm({ ...shiftForm, staff_breakdown: next })}
                rates={shiftForm.hourly_rate_breakdown}
                onRatesChange={(next) => setShiftForm({ ...shiftForm, hourly_rate_breakdown: next })}
                currency={shiftForm.currency}
                onCurrencyChange={(val) => setShiftForm({ ...shiftForm, currency: val })}
                roles={activeRoles}
                testIdPrefix="create"
                optional
                helperText={
                  "Optional planning tool. Leave all counts at 0 to save the event without a per-role breakdown. " +
                  "Fill in one or more roles to auto-set the event's total capacity."
                }
              />

              {/* Assign staff now — direct assignment, skips approval queue. */}
              <AssignStaffNowBlock
                value={shiftForm.assign_now_ids || []}
                onChange={(next) => setShiftForm({ ...shiftForm, assign_now_ids: next })}
                users={allUsers}
              />

              {/* Shift Category — drives Retail vs Event/Festival grouping on the Shift Calendar */}
              <div className="space-y-2 p-3 bg-muted/50 border border-border rounded-sm">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <CalendarPlus className="w-4 h-4" /> Shift Category
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={shiftForm.shift_category === 'retail' ? 'default' : 'outline'}
                    onClick={() => setShiftForm({ ...shiftForm, shift_category: 'retail' })}
                    className={cn(
                      "rounded-sm flex-1 uppercase tracking-wider",
                      shiftForm.shift_category === 'retail' && "bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                    )}
                    data-testid="event-category-retail-btn"
                  >
                    Retail
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={shiftForm.shift_category === 'event' ? 'default' : 'outline'}
                    onClick={() => setShiftForm({ ...shiftForm, shift_category: 'event' })}
                    className={cn(
                      "rounded-sm flex-1 uppercase tracking-wider",
                      shiftForm.shift_category === 'event' && "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                    )}
                    data-testid="event-category-event-btn"
                  >
                    Event / Festival
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground/70">
                  Staff in this event will appear under {shiftForm.shift_category === 'retail' ? 'Retail Staff' : 'Event/Festival Staff'} on the Shift Calendar.
                </p>
              </div>

              {/* Camping Section */}
              <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Tent className="w-4 h-4 text-green-400" /> Camping Provided
                  </Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={shiftForm.camping_provided === true ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, camping_provided: true })} className={cn("rounded-sm", shiftForm.camping_provided === true && "bg-green-600")}>
                      Yes
                    </Button>
                    <Button type="button" size="sm" variant={shiftForm.camping_provided === false ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, camping_provided: false })} className={cn("rounded-sm", shiftForm.camping_provided === false && "bg-secondary")}>
                      No
                    </Button>
                  </div>
                </div>
              </div>

              {/* Accommodation Section */}
              <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Home className="w-4 h-4 text-blue-400" /> Accommodation Provided
                  </Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={shiftForm.accommodation_provided === true ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, accommodation_provided: true })} className={cn("rounded-sm", shiftForm.accommodation_provided === true && "bg-green-600")}>
                      Yes
                    </Button>
                    <Button type="button" size="sm" variant={shiftForm.accommodation_provided === false ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, accommodation_provided: false })} className={cn("rounded-sm", shiftForm.accommodation_provided === false && "bg-secondary")}>
                      No
                    </Button>
                  </div>
                </div>
                {shiftForm.accommodation_provided === true && (
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cost Per Staff Per Day (£)</Label>
                    <Input type="number" step="0.01" min="0" value={shiftForm.accommodation_cost_per_day} onChange={(e) => setShiftForm({ ...shiftForm, accommodation_cost_per_day: e.target.value })} placeholder="e.g., 25.00" className="bg-background border-border rounded-sm" />
                  </div>
                )}
                {shiftForm.accommodation_provided === false && (
                  <p className="text-sm text-amber-400 flex items-center gap-2 pt-2 border-t border-border">
                    <AlertTriangle className="w-4 h-4" />
                    Staff will see: "You have to sort your own accommodation"
                  </p>
                )}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Accommodation Notes</Label>
                  <Textarea value={shiftForm.accommodation_notes} onChange={(e) => setShiftForm({ ...shiftForm, accommodation_notes: e.target.value })} placeholder="Any notes about accommodation..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="accommodation-notes-input" />
                </div>
              </div>

              {/* Transport Section */}
              <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Car className="w-4 h-4 text-purple-400" /> Transport Provided
                  </Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={shiftForm.transport_provided === true ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, transport_provided: true })} className={cn("rounded-sm", shiftForm.transport_provided === true && "bg-green-600")}>
                      Yes
                    </Button>
                    <Button type="button" size="sm" variant={shiftForm.transport_provided === false ? 'default' : 'outline'} onClick={() => setShiftForm({ ...shiftForm, transport_provided: false })} className={cn("rounded-sm", shiftForm.transport_provided === false && "bg-secondary")}>
                      No
                    </Button>
                  </div>
                </div>
                {shiftForm.transport_provided === true && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup Location</Label>
                      <Input value={shiftForm.transport_location} onChange={(e) => setShiftForm({ ...shiftForm, transport_location: e.target.value })} placeholder="e.g., London Office" className="bg-background border-border rounded-sm" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Departure Time</Label>
                      <Input type="time" value={shiftForm.transport_departure_time} onChange={(e) => setShiftForm({ ...shiftForm, transport_departure_time: e.target.value })} className="bg-background border-border rounded-sm" />
                    </div>
                  </div>
                )}
                {shiftForm.transport_provided === false && (
                  <p className="text-sm text-amber-400 flex items-center gap-2 pt-2 border-t border-border">
                    <AlertTriangle className="w-4 h-4" />
                    Staff will see: "You have to make your own way"
                  </p>
                )}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Transport Notes</Label>
                  <Textarea value={shiftForm.transport_notes} onChange={(e) => setShiftForm({ ...shiftForm, transport_notes: e.target.value })} placeholder="Any notes about transport..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="transport-notes-input" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Notes / Instructions
                </Label>
                <Textarea value={shiftForm.notes} onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })} placeholder="Any additional instructions..." className="bg-background border-border rounded-sm resize-none" rows={2} />
              </div>

              {/* Summary */}
              {!editShift && shiftForm.selectedDates.length > 0 && (() => {
                const bdTotal =
                  (parseInt(shiftForm.staff_breakdown?.sia_door_supervisor) || 0) +
                  (parseInt(shiftForm.staff_breakdown?.cso_steward) || 0) +
                  (parseInt(shiftForm.staff_breakdown?.cleaner) || 0) +
                  (parseInt(shiftForm.staff_breakdown?.supervisor) || 0);
                if (bdTotal <= 0) return null;
                return (
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                    <p className="text-sm text-blue-400" data-testid="create-shift-summary">
                      This will create <strong>{shiftForm.selectedDates.length * bdTotal}</strong> shift slots
                      ({shiftForm.selectedDates.length} days × {bdTotal} staff)
                    </p>
                  </div>
                );
              })()}
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {!editShift && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addEvent}
                    className="border-dashed border-primary text-primary hover:bg-primary/10 rounded-sm sm:mr-auto"
                    data-testid="add-event-footer-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Add Another Event
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setEditShift(null); resetForm(); }} className="border-border rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-open-shift-btn" disabled={creating} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                  {creating ? 'Saving...' : (editShift ? 'Update Shift' : `Save ${eventForms.length > 1 ? `All ${eventForms.length} Events` : 'Shifts'}`)}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Staff Dialog */}
        <Dialog open={!!manageShift} onOpenChange={(open) => { if (!open) { setManageShift(null); setManageGroup(null); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">Manage Staff</DialogTitle>
            </DialogHeader>
            
            {manageShift && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-accent/50 rounded-sm space-y-1">
                  <p className="font-medium">{manageShift.position || 'TBD'}</p>
                  <p className="text-sm text-muted-foreground">{manageShift.location || 'TBD'}</p>
                  <p className="text-sm text-muted-foreground">{formatDate(manageShift.date)}</p>
                  <p className="text-[11px] text-muted-foreground/70 italic">Times are set per staff in Shift Calendar</p>
                </div>

                {/* Day switcher — flip between the event's days without
                    closing the dialog. */}
                {manageGroup && (manageGroup.shifts || []).length > 1 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="manage-day-switcher">
                    {[...manageGroup.shifts].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((s) => (
                      <Button
                        key={s.id}
                        type="button"
                        size="sm"
                        variant={s.id === manageShift.id ? 'default' : 'outline'}
                        onClick={() => setManageShift(s)}
                        className={cn(
                          'rounded-sm text-xs h-7 px-2',
                          s.id === manageShift.id
                            ? 'bg-primary text-primary-foreground hover:bg-yellow-300'
                            : 'border-border text-muted-foreground hover:text-foreground'
                        )}
                        data-testid={`manage-day-${s.date}`}
                      >
                        {formatDate(s.date)}
                      </Button>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSendReminder(manageShift.id)} className="flex-1 border-border rounded-sm">
                    <Bell className="w-4 h-4 mr-2" /> Send Reminder
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleBroadcast(manageShift.id)} className="flex-1 border-border rounded-sm">
                    <Megaphone className="w-4 h-4 mr-2" /> Broadcast
                  </Button>
                </div>

                {/* Danger zone — per-day delete. Opens the dedicated confirmation
                    dialog so admin sees exactly what happens (only this date is
                    removed; other days of the event are untouched). */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDayDialog({ open: true, shift: manageShift, group: manageGroup })}
                  className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-sm"
                  data-testid={`manage-delete-day-${manageShift.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete This Event Day
                </Button>

                {/* Admin Time Adjustment — opens the shared dialog in
                    scope='select' so admin can adjust every employee's
                    times at once, uncheck people who left early, or edit
                    just one. Fetch is on-demand so an unopened Manage
                    Event card doesn't waste bandwidth. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAdjustDialog(manageShift)}
                  className="w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm"
                  data-testid={`manage-adjust-times-${manageShift.id}`}
                >
                  <Clock className="w-4 h-4 mr-2" /> Adjust Actual Times
                </Button>

                <div className="p-3 bg-accent/30 rounded-sm">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    Coverage: {manageShift.claimed_count || 0} / {manageShift.quantity || 1} staff assigned
                  </p>
                  <div className="mt-2 h-2 bg-accent rounded-full overflow-hidden">
                    <div className={cn("h-full transition-all", (manageShift.claimed_count || 0) >= (manageShift.quantity || 1) ? "bg-green-500" : "bg-blue-500")} style={{ width: `${Math.min(100, ((manageShift.claimed_count || 0) / (manageShift.quantity || 1)) * 100)}%` }} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Assigned Staff</Label>
                  {manageShift.claimed_by_list?.length > 0 ? (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {manageShift.claimed_by_list.map((staff) => (
                        <div key={staff.user_id} className="flex items-center justify-between p-2 bg-accent rounded-sm hover:bg-primary/15 transition-colors">
                          <button
                            type="button"
                            onClick={() => openScheduleDialogForStaff(manageGroup || { key: manageShift.id, shifts: [manageShift] }, staff)}
                            className="flex-1 flex items-center gap-2 text-left cursor-pointer"
                            title="Set this staff's hours per day of the event"
                            data-testid={`schedule-staff-${staff.user_id}`}
                          >
                            <User className="w-4 h-4 text-muted-foreground" />
                            <span className="hover:text-primary transition-colors">{staff.user_name}</span>
                            <ComplianceIndicator userId={staff.user_id} />
                            <Badge variant="outline" className="text-xs">{staff.user_role}</Badge>
                            {staff.is_extra && (
                              <Badge className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border-amber-500/40">+ Extra</Badge>
                            )}
                            <Clock className="w-3.5 h-3.5 text-muted-foreground ml-1 opacity-60" title="Click to set hours" />
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setStaffScheduleView({ open: true, staff })}
                            className="text-primary hover:text-primary hover:bg-primary/10"
                            title="View schedule (read-only)"
                            data-testid={`view-staff-schedule-${staff.user_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReplaceStaffDialog({ open: true, staff })}
                            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                            title="Replace this staff (keep their schedule)"
                            data-testid={`replace-staff-${staff.user_id}`}
                          >
                            <ReplaceIcon className="w-4 h-4" />
                          </Button>
                          {!opsMode && (() => {
                            const u = allUsers.find((x) => x.id === staff.user_id);
                            const onShift = !!u?.is_on_shift;
                            return (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setManualClockDialog({
                                  open: true,
                                  employee: {
                                    id: staff.user_id,
                                    name: staff.user_name,
                                    is_on_shift: onShift,
                                  },
                                  action: onShift ? 'out' : 'in',
                                  shiftId: manageShift?.id || null,
                                })}
                                className={cn(
                                  onShift
                                    ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'
                                    : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10',
                                )}
                                title={onShift ? 'Manual Clock Out' : 'Manual Clock In'}
                                data-testid={`manual-clock-${staff.user_id}`}
                              >
                                {onShift ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                              </Button>
                            );
                          })()}
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveStaff(manageShift.id, staff.user_id)} className="text-destructive hover:text-destructive hover:bg-destructive/10">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground p-2">No staff assigned yet</p>
                  )}
                </div>

                {(manageShift.claimed_count || 0) < (manageShift.quantity || 1) && (
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <UserPlus className="w-4 h-4" /> Add Staff to {formatDate(manageShift.date)}
                    </Label>
                    {manageGroup && manageGroup.shifts.length > 1 && (
                      <p className="text-[11px] text-muted-foreground">
                        Adds only to this date. Use the day-chips on the event card for other dates,
                        or "Add to All Days" below.
                      </p>
                    )}
                    <Input placeholder="Search staff..." value={staffSearchTerm} onChange={(e) => setStaffSearchTerm(e.target.value)} className="bg-background border-border rounded-sm" />
                    {staffSearchTerm && (
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {filteredUsers.filter(u => !manageShift.claimed_by_list?.some(c => c.user_id === u.id)).slice(0, 10).map(u => {
                          // Count how many days of the multi-day event this user is already on
                          const otherDayCount = manageGroup
                            ? manageGroup.shifts.filter(s => s.id !== manageShift.id && (s.claimed_by_list || []).some(c => c.user_id === u.id)).length
                            : 0;
                          const totalOtherDays = manageGroup ? (manageGroup.shifts.length - 1) : 0;
                          return (
                            <div key={u.id} className="flex items-stretch gap-1">
                              <button
                                onClick={() => handleAddStaff(manageShift.id, u.id)}
                                className="flex-1 flex items-center justify-between p-2 bg-accent hover:bg-primary/15 rounded-sm text-left transition-colors"
                                data-testid={`manage-add-${u.id}`}
                              >
                                <span className="flex items-center gap-2">
                                  <span>{u.name}</span>
                                  <Badge variant="outline" className="text-xs">{u.role}</Badge>
                                </span>
                                {otherDayCount > 0 && (
                                  <span className="text-[10px] text-green-400/80">
                                    on {otherDayCount}/{totalOtherDays} other day{totalOtherDays !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </button>
                              {manageGroup && manageGroup.shifts.length > 1 && (
                                <button
                                  onClick={() => handleAddStaffToAllDays(u.id)}
                                  className="px-2 bg-accent hover:bg-primary/20 border border-border rounded-sm text-[10px] uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
                                  title="Add to all days of this event"
                                  data-testid={`manage-add-all-${u.id}`}
                                >
                                  All Days
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setManageShift(null)} className="border-border rounded-sm">Close</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Day Capacity Dialog — admin adjusts staff slots per day
            without removing assigned staff. */}
        <Dialog
          open={capacityDialog.open}
          onOpenChange={(open) => { if (!open) { setCapacityDialog({ open: false, group: null }); setCapacityValues({}); } }}
        >
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-capacity-dialog">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                Edit Day Capacity
              </DialogTitle>
              <DialogDescription>
                Adjust the number of staff spaces for each day. You cannot reduce below the number of staff already assigned for that day.
              </DialogDescription>
            </DialogHeader>

            {capacityDialog.group && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-accent/50 rounded-sm space-y-1">
                  <p className="text-sm font-medium">{capacityDialog.group.position}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" />{capacityDialog.group.location}
                  </p>
                </div>

                <div className="space-y-2">
                  {[...capacityDialog.group.shifts]
                    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
                    .map((s) => {
                      const assigned = (s.claimed_by_list || []).length;
                      const current = capacityValues[s.id] ?? s.quantity ?? 1;
                      const belowAssigned = Number(current) < assigned;
                      return (
                        <div
                          key={s.id}
                          className="flex items-center justify-between gap-3 p-3 bg-muted/40 border border-border rounded-sm"
                          data-testid={`capacity-row-${s.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{format(parseISO(s.date), 'EEE, MMM d, yyyy')}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {assigned} assigned · current capacity {s.quantity || 1}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 rounded-sm"
                              onClick={() => setCapacityValues((prev) => ({
                                ...prev,
                                [s.id]: Math.max(assigned || 1, Number(prev[s.id] ?? s.quantity ?? 1) - 1),
                              }))}
                              data-testid={`capacity-dec-${s.id}`}
                            >
                              -
                            </Button>
                            <Input
                              type="number"
                              min={Math.max(1, assigned)}
                              value={current}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                setCapacityValues((prev) => ({
                                  ...prev,
                                  [s.id]: Number.isNaN(v) ? '' : v,
                                }));
                              }}
                              className={cn(
                                'w-20 h-8 text-center bg-background border-border rounded-sm',
                                belowAssigned && 'border-destructive text-destructive'
                              )}
                              data-testid={`capacity-input-${s.id}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 rounded-sm"
                              onClick={() => setCapacityValues((prev) => ({
                                ...prev,
                                [s.id]: Number(prev[s.id] ?? s.quantity ?? 1) + 1,
                              }))}
                              data-testid={`capacity-inc-${s.id}`}
                            >
                              +
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => { setCapacityDialog({ open: false, group: null }); setCapacityValues({}); }}
                    className="border-border rounded-sm"
                    data-testid="capacity-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveCapacity}
                    disabled={capacitySaving}
                    className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                    data-testid="capacity-save-btn"
                  >
                    {capacitySaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SaveIcon className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Batch Edit Event Dialog */}
        <Dialog open={!!batchEditGroup} onOpenChange={(open) => { if (!open) { setBatchEditGroup(null); setBatchEditForm(null); setBatchEditDates([]); setBatchEditOriginalDates([]); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider uppercase">
                Edit Event ({batchEditDates.length} day{batchEditDates.length !== 1 ? 's' : ''})
              </DialogTitle>
            </DialogHeader>
            {batchEditForm && (
              <form onSubmit={handleBatchUpdate} className="space-y-4 mt-4">
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                  <p className="text-sm text-blue-400">
                    Changes will apply to all <strong>{batchEditDates.length}</strong> day{batchEditDates.length !== 1 ? 's' : ''}. You can add or remove dates below.
                  </p>
                </div>

                {/* Date Edit Calendar */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Edit Dates ({batchEditDates.length} selected)
                  </Label>
                  <div className="bg-muted border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between mb-3">
                      <Button type="button" variant="ghost" size="icon" onClick={() => setBatchEditMonth(m => subMonths(m, 1))} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm font-medium">{getMonthDays(batchEditMonth).monthLabel}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => setBatchEditMonth(m => addMonths(m, 1))} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-7 mb-1">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d, i) => (
                        <div key={i} className="text-center text-xs text-muted-foreground py-1">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: getMonthDays(batchEditMonth).startPadding }).map((_, i) => (
                        <div key={`pad-${i}`} className="h-8" />
                      ))}
                      {getMonthDays(batchEditMonth).days.map((day) => {
                        const isSelected = batchEditDates.includes(day.dateStr);
                        const isOriginal = batchEditOriginalDates.includes(day.dateStr);
                        const isNew = isSelected && !isOriginal;
                        const isRemoved = !isSelected && isOriginal;
                        return (
                          <button
                            key={day.dateStr}
                            type="button"
                            onClick={() => {
                              setBatchEditDates(prev =>
                                prev.includes(day.dateStr) ? prev.filter(d => d !== day.dateStr) : [...prev, day.dateStr]
                              );
                            }}
                            className={cn(
                              "h-8 w-full text-sm rounded-sm flex items-center justify-center transition-all font-medium",
                              isNew ? "bg-green-500 text-white" :
                              isSelected ? "bg-primary text-black" :
                              isRemoved ? "bg-red-500/20 text-red-400 line-through" :
                              day.isToday ? "bg-blue-500/30 text-blue-300" :
                              "hover:bg-accent text-foreground"
                            )}
                            data-testid={`batch-date-${day.dateStr}`}
                          >
                            {day.dayNum}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Date summary */}
                  {batchEditDates.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {batchEditDates.sort().map(d => {
                        const isNew = !batchEditOriginalDates.includes(d);
                        return (
                          <Badge key={d} variant="outline" className={cn("text-xs", isNew ? "bg-green-500/20 text-green-400 border-green-500/50" : "bg-primary/20 text-primary border-primary/50")}>
                            {format(parseISO(d), 'MMM d')}{isNew ? ' (new)' : ''}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  {batchEditOriginalDates.filter(d => !batchEditDates.includes(d)).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {batchEditOriginalDates.filter(d => !batchEditDates.includes(d)).map(d => (
                        <Badge key={d} variant="outline" className="text-xs bg-red-500/20 text-red-400 border-red-500/50 line-through">
                          {format(parseISO(d), 'MMM d')} (removed)
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Briefcase className="w-4 h-4" /> Position</Label>
                    <Input value={batchEditForm.position} onChange={(e) => setBatchEditForm({ ...batchEditForm, position: e.target.value })} className="bg-background border-border rounded-sm" data-testid="batch-edit-position" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><MapPin className="w-4 h-4" /> Location</Label>
                    <Input value={batchEditForm.location} onChange={(e) => setBatchEditForm({ ...batchEditForm, location: e.target.value })} className="bg-background border-border rounded-sm" data-testid="batch-edit-location" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shirt className="w-4 h-4" /> Dress Code</Label>
                  <Input value={batchEditForm.dress_code} onChange={(e) => setBatchEditForm({ ...batchEditForm, dress_code: e.target.value })} className="bg-background border-border rounded-sm" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Time</Label>
                    <Input type="time" value={batchEditForm.start_time} onChange={(e) => setBatchEditForm({ ...batchEditForm, start_time: e.target.value })} className="bg-background border-border rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Time</Label>
                    <Input type="time" value={batchEditForm.end_time} onChange={(e) => setBatchEditForm({ ...batchEditForm, end_time: e.target.value })} className="bg-background border-border rounded-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><PoundSterling className="w-4 h-4" /> Hourly Rate ({currencySymbol(batchEditForm.currency)})</Label>
                    <Input type="number" step="0.01" value={batchEditForm.hourly_payment} onChange={(e) => setBatchEditForm({ ...batchEditForm, hourly_payment: e.target.value })} className="bg-background border-border rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shield className="w-4 h-4" /> SIA Required</Label>
                    <Select value={batchEditForm.sia_required} onValueChange={(val) => setBatchEditForm({ ...batchEditForm, sia_required: val })}>
                      <SelectTrigger className="bg-background border-border rounded-sm"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="door_supervisor">Door Supervisor</SelectItem>
                        <SelectItem value="cctv">CCTV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Staff Requirements — per-role breakdown + rate per role.
                    Applied to every day in the event on save. */}
                <StaffRequirementsBlock
                  value={batchEditForm.staff_breakdown}
                  onChange={(next) => setBatchEditForm({ ...batchEditForm, staff_breakdown: next })}
                  rates={batchEditForm.hourly_rate_breakdown}
                  onRatesChange={(next) => setBatchEditForm({ ...batchEditForm, hourly_rate_breakdown: next })}
                  currency={batchEditForm.currency}
                  onCurrencyChange={(val) => setBatchEditForm({ ...batchEditForm, currency: val })}
                  roles={activeRoles}
                  testIdPrefix="batch"
                  optional
                  helperText={
                    "Leave all four staff counts at 0 to keep the current event capacity unchanged. " +
                    "Set any per-role rate above 0 to override the single Hourly Rate for that role."
                  }
                />

                {/* Shift Category — Retail vs Event/Festival */}
                <div className="p-3 bg-muted/50 border border-border rounded-sm space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4" /> Shift Category
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={batchEditForm.shift_category === 'retail' ? 'default' : 'outline'}
                      onClick={() => setBatchEditForm({ ...batchEditForm, shift_category: 'retail' })}
                      className={cn(
                        "rounded-sm flex-1 uppercase tracking-wider",
                        batchEditForm.shift_category === 'retail' && "bg-blue-600 hover:bg-blue-700 text-white border-blue-600"
                      )}
                      data-testid="batch-event-category-retail-btn"
                    >
                      Retail
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={(batchEditForm.shift_category || 'event') === 'event' ? 'default' : 'outline'}
                      onClick={() => setBatchEditForm({ ...batchEditForm, shift_category: 'event' })}
                      className={cn(
                        "rounded-sm flex-1 uppercase tracking-wider",
                        (batchEditForm.shift_category || 'event') === 'event' && "bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                      )}
                      data-testid="batch-event-category-event-btn"
                    >
                      Event / Festival
                    </Button>
                  </div>
                </div>

                {/* Camping */}
                <div className="p-4 bg-muted border border-border rounded-sm">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Tent className="w-4 h-4 text-green-400" /> Camping</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={batchEditForm.camping_provided === true ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, camping_provided: true })} className={cn("rounded-sm", batchEditForm.camping_provided === true && "bg-green-600")}>Yes</Button>
                      <Button type="button" size="sm" variant={batchEditForm.camping_provided === false ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, camping_provided: false })} className={cn("rounded-sm", batchEditForm.camping_provided === false && "bg-secondary")}>No</Button>
                    </div>
                  </div>
                </div>

                {/* Accommodation */}
                <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Home className="w-4 h-4 text-blue-400" /> Accommodation</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={batchEditForm.accommodation_provided === true ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, accommodation_provided: true })} className={cn("rounded-sm", batchEditForm.accommodation_provided === true && "bg-green-600")}>Yes</Button>
                      <Button type="button" size="sm" variant={batchEditForm.accommodation_provided === false ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, accommodation_provided: false })} className={cn("rounded-sm", batchEditForm.accommodation_provided === false && "bg-secondary")}>No</Button>
                    </div>
                  </div>
                  {batchEditForm.accommodation_provided === true && (
                    <div className="space-y-2 pt-2 border-t border-border">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cost Per Staff Per Day (£)</Label>
                      <Input type="number" step="0.01" value={batchEditForm.accommodation_cost_per_day} onChange={(e) => setBatchEditForm({ ...batchEditForm, accommodation_cost_per_day: e.target.value })} className="bg-background border-border rounded-sm" />
                    </div>
                  )}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Accommodation Notes</Label>
                    <Textarea value={batchEditForm.accommodation_notes} onChange={(e) => setBatchEditForm({ ...batchEditForm, accommodation_notes: e.target.value })} placeholder="Any notes about accommodation..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="batch-accommodation-notes" />
                  </div>
                </div>

                {/* Transport */}
                <div className="p-4 bg-muted border border-border rounded-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Car className="w-4 h-4 text-purple-400" /> Transport</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={batchEditForm.transport_provided === true ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, transport_provided: true })} className={cn("rounded-sm", batchEditForm.transport_provided === true && "bg-green-600")}>Yes</Button>
                      <Button type="button" size="sm" variant={batchEditForm.transport_provided === false ? 'default' : 'outline'} onClick={() => setBatchEditForm({ ...batchEditForm, transport_provided: false })} className={cn("rounded-sm", batchEditForm.transport_provided === false && "bg-secondary")}>No</Button>
                    </div>
                  </div>
                  {batchEditForm.transport_provided === true && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pickup Location</Label>
                        <Input value={batchEditForm.transport_location} onChange={(e) => setBatchEditForm({ ...batchEditForm, transport_location: e.target.value })} className="bg-background border-border rounded-sm" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Departure Time</Label>
                        <Input type="time" value={batchEditForm.transport_departure_time} onChange={(e) => setBatchEditForm({ ...batchEditForm, transport_departure_time: e.target.value })} className="bg-background border-border rounded-sm" />
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 pt-2 border-t border-border">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Transport Notes</Label>
                    <Textarea value={batchEditForm.transport_notes} onChange={(e) => setBatchEditForm({ ...batchEditForm, transport_notes: e.target.value })} placeholder="Any notes about transport..." className="bg-background border-border rounded-sm resize-none" rows={2} data-testid="batch-transport-notes" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><FileText className="w-4 h-4" /> Notes / Instructions</Label>
                  <Textarea value={batchEditForm.notes} onChange={(e) => setBatchEditForm({ ...batchEditForm, notes: e.target.value })} placeholder="Any additional instructions..." className="bg-background border-border rounded-sm resize-none" rows={2} />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setBatchEditGroup(null); setBatchEditForm(null); }} className="border-border rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={creating} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm" data-testid="batch-edit-save-btn">
                    {creating ? 'Saving...' : `Update All ${batchEditDates?.length || 0} Days`}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Per-Staff Schedule Dialog — set hours per-day across event days */}
        <Dialog open={scheduleDialog.open} onOpenChange={(open) => { if (!open) { setScheduleDialog(prev => ({ ...prev, open: false })); setBulkDeleteMode(false); setBulkDeleteIds([]); } }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[92vh] overflow-y-auto" data-testid="schedule-dialog">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                SET HOURS — {scheduleDialog.staff?.user_name?.toUpperCase()}
              </DialogTitle>
              <DialogDescription>
                {scheduleDialog.group?.position} @ {scheduleDialog.group?.location}
              </DialogDescription>
            </DialogHeader>
            {scheduleDialog.staff && (
              <div className="space-y-4 pt-2">
                {/* Per-day shifts, grouped by event so admin sees overlapping
                    events at-a-glance. Each shift has its own checkbox. */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Shifts on these dates</Label>
                    {bulkDeleteMode ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={handleBulkDeleteShifts}
                          disabled={bulkDeleteIds.length === 0}
                          data-testid="bulk-delete-confirm-btn"
                          className="h-7 text-[11px] uppercase tracking-wider rounded-sm"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete {bulkDeleteIds.length > 0 ? `(${bulkDeleteIds.length})` : ''}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setBulkDeleteMode(false); setBulkDeleteIds([]); }}
                          className="h-7 text-[11px] uppercase tracking-wider rounded-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setBulkDeleteMode(true); setBulkDeleteIds([]); }}
                        data-testid="bulk-delete-mode-btn"
                        className="h-7 text-[11px] uppercase tracking-wider rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Bulk Delete
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {bulkDeleteMode ? (
                      <><strong className="text-destructive">BULK DELETE MODE</strong> — tap the red boxes to pick duplicates, then press Delete.</>
                    ) : (
                      <>Only <strong>PENDING</strong> shifts are pre-checked. Tap to add or remove. Set Start &amp; End below, then Apply. Use <strong>+ Split</strong> to add a 2nd interval on the same day (e.g. 10-14 + 16-23). Use ⓧ to clear → Pending.</>
                    )}
                  </p>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1 mt-2">
                    {scheduleDialog.employeeShifts.length === 0 && (
                      <p className="text-sm text-muted-foreground italic p-2">No shifts found for this staff in this event's date range.</p>
                    )}
                    {(() => {
                      // Group: ONE group for "this event" (all shifts whose
                      // from_open_shift is in the current event's open_shift
                      // IDs) + separate groups per OTHER event (keyed by
                      // site+position so multi-day other events stay merged).
                      const thisEventOpenShiftIds = new Set(scheduleDialog.group?.shifts?.map(s => s.id) || []);
                      const groups = {};
                      const groupOrder = [];
                      const ensure = (key, label, isThis) => {
                        if (!groups[key]) {
                          groupOrder.push(key);
                          groups[key] = { key, label, isThis, shifts: [] };
                        }
                        return groups[key];
                      };
                      scheduleDialog.employeeShifts.forEach(sh => {
                        const inThis = sh.from_open_shift && thisEventOpenShiftIds.has(sh.from_open_shift);
                        if (inThis) {
                          ensure('__this__', `${sh.position || ''} @ ${sh.site_name || ''}`, true).shifts.push(sh);
                        } else {
                          const k = `${sh.from_open_shift || 'standalone'}-${sh.site_name || ''}-${sh.position || ''}`;
                          ensure(k, `${sh.position || '—'} @ ${sh.site_name || '—'}`, false).shifts.push(sh);
                        }
                      });
                      return groupOrder.map(key => {
                        const grp = groups[key];
                        return (
                          <div key={key} className={cn(
                            'rounded-sm border p-2 space-y-1.5',
                            grp.isThis ? 'border-primary/40 bg-primary/5' : 'border-amber-500/30 bg-amber-500/5'
                          )}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <p className="text-[11px] uppercase tracking-wider font-semibold">
                                {grp.isThis ? (
                                  <span className="text-primary">This event</span>
                                ) : (
                                  <span className="text-amber-300">Other event (same dates)</span>
                                )}
                                <span className="ml-1.5 text-muted-foreground/80 normal-case font-normal">— {grp.label}</span>
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  const groupShiftIds = grp.shifts.filter(s => s.status === 'scheduled').map(s => s.id);
                                  setScheduleDialog(prev => {
                                    const allPicked = groupShiftIds.every(id => prev.selectedDates.includes(id));
                                    return {
                                      ...prev,
                                      selectedDates: allPicked
                                        ? prev.selectedDates.filter(id => !groupShiftIds.includes(id))
                                        : [...new Set([...prev.selectedDates, ...groupShiftIds])]
                                    };
                                  });
                                }}
                                className="text-[10px] text-primary hover:underline uppercase tracking-wider"
                                data-testid={`select-group-${key}`}
                              >
                                Toggle all in group
                              </button>
                            </div>
                            {grp.shifts.map((sh) => {
                              const picked = scheduleDialog.selectedDates.includes(sh.id);
                              const hasTimes = sh.start_time && sh.end_time;
                              const locked = sh.status === 'in_progress' || sh.status === 'completed';
                              const bulkPicked = bulkDeleteIds.includes(sh.id);
                              return (
                                <div
                                  key={sh.id}
                                  className={cn(
                                    'flex items-center gap-2 p-2 rounded-sm border transition-colors',
                                    locked ? 'bg-muted/30 border-border opacity-70' :
                                    bulkDeleteMode && bulkPicked ? 'bg-destructive/15 border-destructive/50' :
                                    picked ? 'bg-primary/15 border-primary/40' : 'bg-background border-border hover:border-primary/30'
                                  )}
                                  data-testid={`schedule-shift-${sh.id}`}
                                >
                                  {bulkDeleteMode ? (
                                    <input
                                      type="checkbox"
                                      checked={bulkPicked}
                                      disabled={locked}
                                      onChange={() => toggleBulkSelect(sh.id)}
                                      className="w-4 h-4 accent-destructive cursor-pointer"
                                      data-testid={`bulk-pick-${sh.id}`}
                                    />
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={picked}
                                      disabled={locked}
                                      onChange={() => toggleScheduleDate(sh.id)}
                                      className="w-4 h-4 accent-primary cursor-pointer"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium flex items-center gap-1.5">
                                      {format(parseISO(sh.date), 'EEE, MMM d')}
                                      {sh.is_split && <Badge className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border-amber-500/40">Split</Badge>}
                                      {sh.is_extra && <Badge className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border-amber-500/40">+ Extra</Badge>}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      {hasTimes ? (
                                        <span className="font-mono text-foreground">{sh.start_time} - {sh.end_time}</span>
                                      ) : (
                                        <span className="text-amber-400 font-semibold uppercase tracking-wider text-[10px] px-1 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-sm">Pending</span>
                                      )}
                                      {locked && <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{sh.status.replace('_', ' ')}</Badge>}
                                    </div>
                                  </div>
                                  {!locked && (
                                    <div className="flex items-center gap-1">
                                      {hasTimes && (
                                        <button
                                          type="button"
                                          onClick={() => handleClearScheduleDay(sh)}
                                          className="text-muted-foreground hover:text-amber-400 transition-colors p-1"
                                          title="Clear → Pending"
                                          data-testid={`clear-day-${sh.id}`}
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (window.confirm(`Delete this shift (${format(parseISO(sh.date), 'EEE, MMM d')}) for ${scheduleDialog.staff?.name || 'this staff'}? This cannot be undone.`)) {
                                            handleDeleteScheduledShift(sh);
                                          }
                                        }}
                                        className="text-muted-foreground hover:text-destructive transition-colors p-1"
                                        title={sh.is_split ? 'Remove this split' : 'Delete this shift'}
                                        data-testid={`delete-shift-${sh.id}`}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {/* +Split per UNIQUE date in this group */}
                            {[...new Set(grp.shifts.map(s => s.date))].map(d => {
                              const sourceForDate = grp.shifts.find(s => s.date === d && s.status === 'scheduled');
                              if (!sourceForDate) return null;
                              return (
                                <button
                                  key={`split-${key}-${d}`}
                                  type="button"
                                  onClick={() => handleAddSplit(sourceForDate)}
                                  className="text-[10px] uppercase tracking-wider text-amber-400 hover:text-amber-300 hover:underline pl-6"
                                  data-testid={`add-split-${d}`}
                                >
                                  + Split shift on {format(parseISO(d), 'EEE d')}
                                </button>
                              );
                            })}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Form: start/end + apply button */}
                <div className="pt-2 border-t border-border space-y-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" /> Hours to apply
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start</Label>
                      <Input
                        type="time"
                        value={scheduleDialog.startTime}
                        onChange={(e) => setScheduleDialog(prev => ({ ...prev, startTime: e.target.value }))}
                        className="bg-background border-border rounded-sm h-9"
                        data-testid="schedule-start-time"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">End</Label>
                      <Input
                        type="time"
                        value={scheduleDialog.endTime}
                        onChange={(e) => setScheduleDialog(prev => ({ ...prev, endTime: e.target.value }))}
                        className="bg-background border-border rounded-sm h-9"
                        data-testid="schedule-end-time"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                    <span>
                      {scheduleDialog.selectedDates.length === 0 ? 'No shifts picked' : `Will apply to ${scheduleDialog.selectedDates.length} shift${scheduleDialog.selectedDates.length !== 1 ? 's' : ''}`}
                    </span>
                    <div className="flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={() => setScheduleDialog(prev => ({ ...prev, selectedDates: prev.employeeShifts.filter(s => s.status === 'scheduled').map(s => s.id) }))}
                        className="text-primary hover:underline"
                      >
                        Select all
                      </button>
                      <span className="text-muted-foreground/50">|</span>
                      <button
                        type="button"
                        onClick={() => setScheduleDialog(prev => ({ ...prev, selectedDates: [] }))}
                        className="text-muted-foreground hover:underline"
                      >
                        Clear
                      </button>
                      <span className="text-muted-foreground/50">|</span>
                      <button
                        type="button"
                        onClick={async () => {
                          if (scheduleDialog.selectedDates.length === 0) {
                            toast.error('Select at least one shift first');
                            return;
                          }
                          if (!window.confirm(`Delete ${scheduleDialog.selectedDates.length} selected shift(s) for ${scheduleDialog.staff?.name || 'this staff'}? They will be removed from the event card too. This cannot be undone.`)) return;
                          try {
                            const res = await axios.post(`${API}/shifts/bulk-delete`, { shift_ids: scheduleDialog.selectedDates });
                            toast.success(res.data?.message || 'Selected shifts deleted');
                            setScheduleDialog(prev => ({ ...prev, selectedDates: [] }));
                            await reloadScheduleDialog(scheduleDialog.group, scheduleDialog.staff);
                            fetchOpenShifts();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Delete failed');
                          }
                        }}
                        className="text-destructive hover:underline font-semibold"
                        data-testid="delete-selected-shifts-btn"
                      >
                        Delete selected
                      </button>
                    </div>
                  </div>
                  <Button
                    onClick={handleApplyScheduleTimes}
                    disabled={scheduleDialog.saving || !scheduleDialog.startTime || !scheduleDialog.endTime || scheduleDialog.selectedDates.length === 0}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm h-10 font-semibold"
                    data-testid="apply-schedule-times"
                  >
                    {scheduleDialog.saving
                      ? 'Saving...'
                      : `Apply ${scheduleDialog.startTime || '--:--'} - ${scheduleDialog.endTime || '--:--'} to ${scheduleDialog.selectedDates.length} shift${scheduleDialog.selectedDates.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setScheduleDialog(prev => ({ ...prev, open: false }))} className="border-border rounded-sm">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Set Schedule For All Staff Dialog */}
        <Dialog open={setTimesDialog.open} onOpenChange={(open) => { if (!open) setSetTimesDialog({ open: false, group: null, start_time: '', end_time: '' }); }}>
          <DialogContent className="bg-card border-border rounded-sm" data-testid="set-times-dialog">
            <DialogHeader>
              <DialogTitle className="font-barlow tracking-wider flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                SET SCHEDULE FOR ALL STAFF
              </DialogTitle>
              <DialogDescription>
                Apply a single start &amp; end time to every day of this event and every assigned staff member who hasn't been given a custom time yet.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {setTimesDialog.group && (
                <div className="bg-muted/30 border border-border rounded-sm p-3 text-sm space-y-1">
                  <p className="font-medium">{setTimesDialog.group.position} — {setTimesDialog.group.location}</p>
                  <p className="text-xs text-muted-foreground">
                    {setTimesDialog.group.shifts?.length || 0} day{(setTimesDialog.group.shifts?.length || 0) !== 1 ? 's' : ''} · {' '}
                    {setTimesDialog.group.shifts?.reduce((acc, s) => acc + (s.claimed_count || 0), 0)} staff assigned
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="set-times-start" className="text-xs uppercase tracking-wider text-muted-foreground">Start time</Label>
                  <Input
                    id="set-times-start"
                    type="time"
                    value={setTimesDialog.start_time}
                    onChange={(e) => setSetTimesDialog(prev => ({ ...prev, start_time: e.target.value }))}
                    className="rounded-sm h-10"
                    data-testid="set-times-start-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="set-times-end" className="text-xs uppercase tracking-wider text-muted-foreground">End time</Label>
                  <Input
                    id="set-times-end"
                    type="time"
                    value={setTimesDialog.end_time}
                    onChange={(e) => setSetTimesDialog(prev => ({ ...prev, end_time: e.target.value }))}
                    className="rounded-sm h-10"
                    data-testid="set-times-end-input"
                  />
                </div>
              </div>

              <div className="text-xs text-muted-foreground bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 space-y-1">
                <p><strong>What gets updated:</strong> the event default for every day, plus any staff still following the old default (or with no time set).</p>
                <p><strong>What stays untouched:</strong> staff who already have a custom time (set manually from Shift Calendar). In-progress and completed shifts are never modified.</p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setSetTimesDialog({ open: false, group: null, start_time: '', end_time: '' })}
                className="border-border rounded-sm"
                disabled={setTimesSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSetEventTimes}
                disabled={setTimesSaving || !setTimesDialog.start_time || !setTimesDialog.end_time}
                className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                data-testid="confirm-set-times-btn"
              >
                {setTimesSaving ? 'Applying…' : 'Apply to All'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Reset Times Confirmation */}
        <AlertDialog open={resetTimesDialog.open} onOpenChange={(open) => { if (!open) setResetTimesDialog({ open: false, group: null }); }}>
          <AlertDialogContent className="bg-card border-border rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider">RESET TIMES TO PENDING</AlertDialogTitle>
              <AlertDialogDescription>
                This will clear start &amp; end times on every day of this event ({resetTimesDialog.group?.shifts?.length || 0} day{(resetTimesDialog.group?.shifts?.length || 0) !== 1 ? 's' : ''}) and on every assigned staff's shift that is still <strong>scheduled</strong>.
                Shifts already in progress or completed will <strong>not</strong> be touched. You can then set per-staff per-day times from Shift Calendar.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  const g = resetTimesDialog.group;
                  setResetTimesDialog({ open: false, group: null });
                  if (g) await handleResetEventTimes(g);
                }}
                className="bg-amber-500 hover:bg-amber-400 text-black rounded-sm"
                data-testid="confirm-reset-times"
              >
                Reset Times
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent className="bg-card border-border rounded-sm">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider uppercase">Delete Shift</AlertDialogTitle>
              <AlertDialogDescription>Are you sure you want to delete this shift? This cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteShift} className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Full Event Confirmation */}
        <AlertDialog open={cancelEventDialog.open} onOpenChange={(open) => { if (!open) setCancelEventDialog({ open: false, group: null }); }}>
          <AlertDialogContent className="!bg-card border border-border rounded-sm shadow-2xl backdrop-blur-xl" data-testid="delete-full-event-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider">DELETE FULL EVENT</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="block">
                  Are you sure you want to delete this event? This will remove{' '}
                  <strong>all {cancelEventDialog.group?.shifts?.length || 0} day(s)</strong>{' '}
                  and unassign every staff member from the event.
                </span>
                <span className="block mt-2 text-xs text-muted-foreground">
                  Any shifts already clocked-in or completed will be preserved for payroll & history.
                  The Employee, Supervisor and Shift Calendar views will update immediately.
                  This action is written to the Shift Audit Log.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm" data-testid="delete-full-event-cancel">Keep Event</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelEvent} disabled={creating} className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm" data-testid="delete-full-event-confirm">
                {creating ? 'Deleting…' : 'Delete Full Event'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Event Day Confirmation */}
        <AlertDialog open={deleteDayDialog.open} onOpenChange={(open) => { if (!open) setDeleteDayDialog({ open: false, shift: null, group: null }); }}>
          <AlertDialogContent className="!bg-card border border-border rounded-sm shadow-2xl backdrop-blur-xl" data-testid="delete-event-day-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-barlow tracking-wider">DELETE THIS EVENT DAY</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="block">
                  Delete just the <strong>{deleteDayDialog.shift?.date}</strong> day of{' '}
                  <strong>{deleteDayDialog.group?.location || 'this event'}</strong>?
                </span>
                <span className="block mt-2 text-xs text-muted-foreground">
                  Other days of this event will remain untouched. All{' '}
                  {(deleteDayDialog.shift?.claimed_by_list?.length) || 0} assigned staff will lose
                  visibility of the day; any shifts already clocked-in or completed on this day are
                  preserved for history.
                  This action is written to the Shift Audit Log.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border rounded-sm" data-testid="delete-event-day-cancel">Keep Day</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteEventDay} disabled={creating} className="bg-destructive text-destructive-foreground hover:bg-red-600 rounded-sm" data-testid="delete-event-day-confirm">
                {creating ? 'Deleting…' : 'Delete This Day'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Excel Dialog */}
        <ImportExcelDialog
          open={importExcelOpen}
          onOpenChange={(o) => { setImportExcelOpen(o); if (!o) setImportTargetGroup(null); }}
          onImported={fetchOpenShifts}
          targetGroup={importTargetGroup}
          allStaff={allUsers}
        />

        {/* Event Schedule Dialog */}
        <EventScheduleDialog
          open={!!scheduleViewGroup}
          onOpenChange={(o) => { if (!o) setScheduleViewGroup(null); }}
          group={scheduleViewGroup}
          refreshToken={scheduleRefresh}
          onEditStaff={(staff) => openScheduleDialogForStaff(scheduleViewGroup, staff)}
        />

        {/* Single-staff schedule view (read-only) */}
        <StaffScheduleViewDialog
          open={staffScheduleView.open}
          onOpenChange={(o) => setStaffScheduleView({ open: o, staff: o ? staffScheduleView.staff : null })}
          group={manageGroup || (manageShift ? { key: manageShift.id, shifts: [manageShift] } : null)}
          staff={staffScheduleView.staff}
        />

        {/* Add Days & Staff dialog — extends an event with extra days and
            assigns selected staff. Times are optional (Pending Schedule). */}
        {addDaysGroup && (
          <Dialog open={!!addDaysGroup} onOpenChange={(o) => { if (!o) closeAddDaysDialog(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="add-days-dialog">
              <DialogHeader>
                <DialogTitle className="font-barlow uppercase tracking-wider flex items-center gap-2">
                  <CalendarPlus className="w-5 h-5 text-emerald-400" />
                  Add Days & Staff
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Extend "{addDaysGroup.location || addDaysGroup.position}" with extra days, pick which existing staff goes on each day, and optionally set custom times.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Step 1 — Pick new dates via mini calendar */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">New days</Label>
                  {(() => {
                    const monthStart = startOfMonth(addDaysCalMonth);
                    const monthEnd = endOfMonth(addDaysCalMonth);
                    // Build a leading offset so the grid starts on Monday
                    const startDow = (getDay(monthStart) + 6) % 7; // 0=Mon
                    const existingDates = new Set((addDaysGroup.shifts || []).map(s => s.date));
                    const cells = [];
                    // Leading blanks from previous month (greyed out)
                    for (let i = startDow; i > 0; i--) {
                      const d = new Date(monthStart);
                      d.setDate(monthStart.getDate() - i);
                      cells.push({ d, inMonth: false });
                    }
                    // Current month days
                    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
                    daysInMonth.forEach(d => cells.push({ d, inMonth: true }));
                    // Trailing days to round to 42 cells (6 weeks)
                    while (cells.length < 42) {
                      const last = cells[cells.length - 1].d;
                      const d = new Date(last);
                      d.setDate(last.getDate() + 1);
                      cells.push({ d, inMonth: false });
                    }
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Button type="button" variant="outline" size="icon"
                            onClick={() => setAddDaysCalMonth(m => subMonths(m, 1))}
                            className="h-8 w-8 border-border"
                            data-testid="add-days-cal-prev">
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <span className="text-sm font-medium tracking-wider" data-testid="add-days-cal-month-label">
                            {format(addDaysCalMonth, 'MMMM yyyy')}
                          </span>
                          <Button type="button" variant="outline" size="icon"
                            onClick={() => setAddDaysCalMonth(m => addMonths(m, 1))}
                            className="h-8 w-8 border-border"
                            data-testid="add-days-cal-next">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {['M','T','W','T','F','S','S'].map((l,i) => (
                            <div key={i} className="text-[9px] text-center text-muted-foreground uppercase">{l}</div>
                          ))}
                          {cells.map(({ d, inMonth }) => {
                            const dStr = format(d, 'yyyy-MM-dd');
                            const alreadyInEvent = existingDates.has(dStr);
                            const selected = addDaysDates.includes(dStr);
                            const dayNum = d.getDate();
                            return (
                              <button
                                key={dStr}
                                type="button"
                                disabled={alreadyInEvent || !inMonth}
                                onClick={() => {
                                  setAddDaysDates((prev) =>
                                    prev.includes(dStr) ? prev.filter(x => x !== dStr) : [...prev, dStr]
                                  );
                                }}
                                className={`h-9 rounded-sm text-xs ${
                                  !inMonth ? 'bg-transparent text-muted-foreground/30 cursor-default' :
                                  alreadyInEvent ? 'bg-muted/30 text-muted-foreground cursor-not-allowed line-through' :
                                  selected ? 'bg-primary text-primary-foreground font-bold' :
                                  'bg-background border border-border hover:bg-accent'
                                }`}
                                title={alreadyInEvent ? 'Already in event' : ''}
                                data-testid={`add-days-cal-${dStr}`}
                              >
                                {dayNum}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  {addDaysDates.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {[...addDaysDates].sort().map((d) => (
                        <Badge key={d} variant="outline" className="text-[10px]">
                          {format(parseISO(d), 'EEE d MMM')}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Step 2 — Pick staff from ALL staff in system + search */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider">Staff to add</Label>
                    <button
                      type="button"
                      onClick={() => {
                        const all = {};
                        (allUsers || []).filter(u => u.role === 'employee' || u.role === 'supervisor').forEach(u => { all[u.id] = true; });
                        setAddDaysStaff(all);
                      }}
                      className="text-[10px] text-primary hover:underline"
                      data-testid="add-days-select-all"
                    >
                      Select all
                    </button>
                  </div>
                  <Input
                    placeholder="Search staff by name..."
                    value={addDaysStaffSearch}
                    onChange={(e) => setAddDaysStaffSearch(e.target.value)}
                    className="bg-background border-border h-8 text-sm"
                    data-testid="add-days-staff-search"
                  />
                  <div className="max-h-56 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                    {(allUsers || [])
                      .filter(u => u.role === 'employee' || u.role === 'supervisor')
                      .filter(u => !addDaysStaffSearch || (u.name || '').toLowerCase().includes(addDaysStaffSearch.toLowerCase()))
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((emp) => (
                        <label key={emp.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-accent/30 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={!!addDaysStaff[emp.id]}
                            onChange={(e) => setAddDaysStaff(prev => ({ ...prev, [emp.id]: e.target.checked }))}
                            data-testid={`add-days-staff-${emp.id}`}
                          />
                          <span className="flex-1 truncate">{emp.name || emp.email}</span>
                          {emp.role === 'supervisor' && (
                            <Badge variant="outline" className="text-[9px] py-0">sup</Badge>
                          )}
                        </label>
                      ))}
                  </div>
                </div>

                {/* Step 3 — Optional per-staff per-day times */}
                {addDaysDates.length > 0 && Object.values(addDaysStaff).some(v => v) && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider">Optional times (leave blank → Pending Schedule)</Label>
                    <div className="max-h-56 overflow-y-auto space-y-2 border border-border rounded-sm p-2">
                      {(allUsers || [])
                        .filter(u => addDaysStaff[u.id])
                        .flatMap((emp) =>
                          [...addDaysDates].sort().map((d) => {
                            const k = `${emp.id}|${d}`;
                            const v = addDaysTimes[k] || {};
                            return (
                              <div key={k} className="flex items-center gap-2 text-xs">
                                <span className="w-32 truncate" title={`${emp.name} · ${d}`}>
                                  {(emp.name || '').split(' ')[0]} · {format(parseISO(d), 'EEE d')}
                                </span>
                                <Input
                                  type="time"
                                  value={v.start || ''}
                                  onChange={(e) => setAddDaysTimes(prev => ({ ...prev, [k]: { ...prev[k], start: e.target.value } }))}
                                  className="h-7 w-[100px] text-xs"
                                  data-testid={`add-days-start-${emp.id}-${d}`}
                                />
                                <span>–</span>
                                <Input
                                  type="time"
                                  value={v.end || ''}
                                  onChange={(e) => setAddDaysTimes(prev => ({ ...prev, [k]: { ...prev[k], end: e.target.value } }))}
                                  className="h-7 w-[100px] text-xs"
                                  data-testid={`add-days-end-${emp.id}-${d}`}
                                />
                              </div>
                            );
                          })
                        )}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeAddDaysDialog} disabled={addDaysSaving}>Cancel</Button>
                <Button onClick={handleSaveAddDays} disabled={addDaysSaving} className="bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="add-days-save">
                  {addDaysSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SaveIcon className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Replace staff dialog */}
        <ReplaceStaffDialog
          open={replaceStaffDialog.open}
          onOpenChange={(o) => setReplaceStaffDialog({ open: o, staff: o ? replaceStaffDialog.staff : null })}
          group={manageGroup || (manageShift ? { key: manageShift.id, shifts: [manageShift] } : null)}
          oldStaff={replaceStaffDialog.staff}
          allUsers={allUsers}
          onReplaced={() => {
            fetchOpenShifts();
            // refresh the Manage Staff dialog body too
            if (manageShift?.id) {
              axios.get(`${API}/open-shifts/${manageShift.id}`).then(r => setManageShift(r.data)).catch(() => {});
            }
          }}
        />

        {/* Admin Shift Time Adjustment — event-day scope with per-employee
            subset picker. Powers the "Adjust Actual Times" button on
            Manage Event so admin can move the finish time back 30 minutes
            for the whole team OR just the people who left early. */}
        <AdjustShiftTimeDialog
          open={adjustDialog.open}
          onOpenChange={(o) => {
            if (!o) setAdjustDialog({ open: false, openShiftId: null, date: null, eventName: '', candidates: [] });
          }}
          scope="select"
          openShiftId={adjustDialog.openShiftId}
          date={adjustDialog.date}
          eventName={adjustDialog.eventName}
          candidates={adjustDialog.candidates}
          onSaved={() => {
            setAdjustDialog({ open: false, openShiftId: null, date: null, eventName: '', candidates: [] });
            fetchOpenShifts();
            if (manageShift) {
              axios.get(`${API}/open-shifts/${manageShift.id}`).then(r => setManageShift(r.data)).catch(() => {});
            }
          }}
        />

        {/* Manual Clock In/Out (admin override) — opened from Assigned Staff row */}
        <ManualClockDialog
          open={manualClockDialog.open}
          onOpenChange={(o) => setManualClockDialog((prev) => ({ ...prev, open: o }))}
          employee={manualClockDialog.employee}
          defaultAction={manualClockDialog.action}
          shiftId={manualClockDialog.shiftId}
          onSuccess={() => {
            // Refresh user list so is_on_shift updates, and refresh the manage dialog
            axios.get(`${API}${opsMode ? '/ops/employees' : '/admin/employees'}`).then((empRes) => {
              axios.get(`${API}/admin/supervisors`).then((supRes) => {
                setAllUsers([...(empRes.data || []), ...(supRes.data || [])]);
              }).catch(() => {});
            }).catch(() => {});
            if (manageShift?.id) {
              axios.get(`${API}/open-shifts/${manageShift.id}`).then(r => setManageShift(r.data)).catch(() => {});
            }
          }}
        />
      </div>
    </Layout>
  );
}
